import { createContext, useContext, useState, ReactNode } from "react";
import { innerPageTranslations } from "./innerPageTranslations";
import { homePageTranslations } from "./homePageTranslations";
import { toolTranslations } from "./toolTranslations";
import { legalTranslations } from "./legalTranslations";
import { guideTranslations } from "./guideTranslations";
import { academyTranslations } from "./academyTranslations";
import { curriculumTranslations } from "./curriculumTranslations";

export type LangCode =
  | "en" | "hi" | "bn" | "te" | "mr" | "ta" | "gu" | "kn" | "ml" | "pa" | "or" | "as" | "ur";

export const LANGUAGES: { code: LangCode; label: string }[] = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी" },
  { code: "kn", label: "ಕನ್ನಡ" },
  { code: "te", label: "తెలుగు" },
  { code: "ta", label: "தமிழ்" },
  { code: "ml", label: "മലയാളം" },
  { code: "mr", label: "मराठी" },
  { code: "bn", label: "বাংলা" },
  { code: "gu", label: "ગુજરાતી" },
  { code: "pa", label: "ਪੰਜਾਬੀ" },
  { code: "or", label: "ଓଡ଼ିଆ" },
  { code: "as", label: "অসমীয়া" },
  { code: "ur", label: "اردو" },
];

// All translatable strings keyed for lookup
type TranslationKeys = {
  // Header
  "nav.tools": string;
  "nav.compare": string;
  "nav.realEstate": string;
  "nav.taxCenter": string;
  "nav.dashboard": string;
  "nav.getStarted": string;

  // Hero
  "hero.badge": string;
  "hero.headline1": string;
  "hero.headline2": string;
  "hero.sub": string;
  "hero.cta1": string;
  "hero.cta2": string;

  // Mini SIP
  "sip.title": string;
  "sip.monthly": string;
  "sip.period": string;
  "sip.yrs": string;
  "sip.invested": string;
  "sip.returns": string;
  "sip.total": string;
  "sip.fullCalc": string;

  // Tool Grid
  "grid.title": string;
  "grid.sub": string;
  "grid.debtTitle": string;
  "grid.debtDesc": string;
  "grid.debtDetail": string;
  "grid.taxTitle": string;
  "grid.taxDesc": string;
  "grid.taxDetail": string;
  "grid.reTitle": string;
  "grid.reDesc": string;
  "grid.reDetail": string;
  "grid.openTool": string;

  // Before/After
  "ba.title": string;
  "ba.sub": string;
  "ba.before": string;
  "ba.after": string;
  "ba.beforeLabel": string;
  "ba.afterLabel": string;
  "ba.b1": string;
  "ba.b2": string;
  "ba.b3": string;
  "ba.b4": string;
  "ba.b5": string;
  "ba.a1": string;
  "ba.a2": string;
  "ba.a3": string;
  "ba.a4": string;
  "ba.a5": string;

  // Hack of Day
  "hack.title": string;
  "hack.tag": string;
  "hack.headline": string;
  "hack.body": string;
  "hack.readMore": string;

  // Comparison
  "comp.title": string;
  "comp.sub": string;
  "comp.rewards": string;
  "comp.annualFee": string;
  "comp.compareAll": string;

  // Wealth Leaks
  "wl.title": string;
  "wl.sub": string;
  "wl.label": string;
  "wl.placeholder": string;
  "wl.hacking": string;
  "wl.hra": string;
  "wl.nps": string;
  "wl.fd": string;
  "wl.deepDive": string;

  // Newsletter
  "nl.title": string;
  "nl.sub": string;
  "nl.placeholder": string;
  "nl.cta": string;
  "nl.success": string;

  // Footer
  "footer.legal": string;
  "footer.privacy": string;
  "footer.disclaimer": string;
};

const en: TranslationKeys = {
  "nav.tools": "Tools",
  "nav.compare": "Compare",
  "nav.realEstate": "Real Estate",
  "nav.taxCenter": "Tax Planner",
  "nav.dashboard": "Dashboard",
  "nav.getStarted": "Get Started",
  "hero.badge": "Smart financial tools for the modern earner",
  "hero.headline1": "Stop Guessing,",
  "hero.headline2": "Start Growing.",
  "hero.sub": "Real-life financial tools to kill debt, invest smarter, and plan your future. Built for earners everywhere.",
  "hero.cta1": "Get Started",
  "hero.cta2": "Explore Tools",
  "sip.title": "Mini SIP Calculator",
  "sip.monthly": "Monthly SIP",
  "sip.period": "Time Period",
  "sip.yrs": "yrs",
  "sip.invested": "Invested",
  "sip.returns": "Est. Returns",
  "sip.total": "Total",
  "sip.fullCalc": "Full Calculator",
  "grid.title": "The Quick Fix Grid",
  "grid.sub": "Three tools. Three money problems solved.",
  "grid.debtTitle": "Debt-Free Path",
  "grid.debtDesc": "Save lakhs in interest.",
  "grid.debtDetail": "Discover exactly how much you overpay on loans and build a prepayment strategy.",
  "grid.taxTitle": "Tax Planner",
  "grid.taxDesc": "New vs Old Regime? Find out in 30s.",
  "grid.taxDetail": "Input your income, deductions, and let the engine recommend the best regime for you.",
  "grid.reTitle": "Real Estate Planner",
  "grid.reDesc": "Can you actually afford that 2BHK?",
  "grid.reDetail": "Factor in EMI, down payment, stamp duty, and maintenance to see the real cost.",
  "grid.openTool": "Open Tool",
  "ba.title": "See the Transformation",
  "ba.sub": "Drag to reveal how VittaSathi optimizes your finances",
  "ba.before": "Before",
  "ba.after": "After",
  "ba.beforeLabel": "Before VittaSathi",
  "ba.afterLabel": "After VittaSathi",
  "ba.b1": "❌ 3 random FDs at different rates",
  "ba.b2": "❌ No idea about HRA exemption",
  "ba.b3": "❌ Paying ₹8,400/mo extra on home loan",
  "ba.b4": "❌ Wrong tax regime — overpaying ₹22K",
  "ba.b5": "❌ No emergency fund strategy",
  "ba.a1": "✅ Consolidated into high-yield debt funds",
  "ba.a2": "✅ HRA claimed — saving ₹12K/mo",
  "ba.a3": "✅ Prepayment plan saves ₹6.2L interest",
  "ba.a4": "✅ Optimal regime — ₹22K refund filed",
  "ba.a5": "✅ 6-month emergency buffer in liquid fund",
  "hack.title": "💡 The 'Hack' of the Day",
  "hack.tag": "Tax Hack #47",
  "hack.headline": "NPS gives you an EXTRA ₹50,000 deduction above 80C",
  "hack.body": "Most salaried Indians max out Section 80C at ₹1.5L and stop. But Section 80CCD(1B) gives you an additional ₹50,000 deduction for NPS contributions. At the 30% tax bracket, that's ₹15,600 saved every year — for doing nothing extra.",
  "hack.readMore": "Read More",
  "comp.title": "Side-by-Side Comparison",
  "comp.sub": "Stop guessing which product is better — see the data",
  "comp.rewards": "Rewards",
  "comp.annualFee": "Annual Fee",
  "comp.compareAll": "Compare All",
  "wl.title": "Find Your Wealth Leaks",
  "wl.sub": "Discover hidden savings you're leaving on the table",
  "wl.label": "Enter your monthly salary",
  "wl.placeholder": "e.g. 75,000",
  "wl.hacking": "🔍 Hidden Hacks You're Missing",
  "wl.hra": "You could save ₹{amount}/mo by claiming HRA correctly",
  "wl.nps": "₹{amount}/yr additional tax deduction via NPS (Sec 80CCD)",
  "wl.fd": "FD interest of ~₹{amount}/yr is taxable — switch to debt funds for indexation benefit",
  "wl.deepDive": "Deep-dive into Tax Planner",
  "nl.title": "Get the Free Wealth Guide",
  "nl.sub": "A 20-page PDF with tax hacks, SIP strategies, and real-estate checklists. Delivered to your inbox — no spam, no fluff.",
  "nl.placeholder": "your@email.com",
  "nl.cta": "Download PDF",
  "nl.success": "🎉 Check your inbox! The guide is on its way.",
  "footer.legal": "Legal Disclaimers",
  "footer.privacy": "Privacy Policy",
  "footer.disclaimer": "© {year} VittaSathi. For informational purposes only. Not financial advice. Consult a certified financial planner before making investment decisions.",
};

const hi: TranslationKeys = {
  "nav.tools": "टूल्स",
  "nav.compare": "तुलना",
  "nav.realEstate": "रियल एस्टेट",
  "nav.taxCenter": "टैक्स प्लानर",
  "nav.dashboard": "डैशबोर्ड",
  "nav.getStarted": "शुरू करें",
  "hero.badge": "आधुनिक भारत के लिए स्मार्ट वित्तीय टूल्स",
  "hero.headline1": "अंदाज़ा लगाना बंद करो,",
  "hero.headline2": "बढ़ना शुरू करो।",
  "hero.sub": "कर्ज़ खत्म करो, टैक्स बचाओ, और अपने सपनों का घर प्लान करो। आधुनिक भारतीय कमाने वालों के लिए बनाया गया।",
  "hero.cta1": "शुरू करें",
  "hero.cta2": "टूल्स देखें",
  "sip.title": "मिनी SIP कैलकुलेटर",
  "sip.monthly": "मासिक SIP",
  "sip.period": "समय अवधि",
  "sip.yrs": "साल",
  "sip.invested": "निवेश",
  "sip.returns": "अनुमानित रिटर्न",
  "sip.total": "कुल",
  "sip.fullCalc": "पूरा कैलकुलेटर",
  "grid.title": "क्विक फिक्स ग्रिड",
  "grid.sub": "तीन टूल्स। तीन पैसे की समस्याएँ हल।",
  "grid.debtTitle": "कर्ज-मुक्ति पथ",
  "grid.debtDesc": "ब्याज में लाखों बचाओ।",
  "grid.debtDetail": "जानो कि आप लोन पर कितना ज़्यादा चुका रहे हो और प्रीपेमेंट स्ट्रैटेजी बनाओ।",
  "grid.taxTitle": "टैक्स प्लानर",
  "grid.taxDesc": "नई vs पुरानी रिजीम? 30 सेकंड में जानो।",
  "grid.taxDetail": "अपनी आय और कटौतियाँ डालो, इंजन सबसे अच्छी रिजीम सुझाएगा।",
  "grid.reTitle": "रियल एस्टेट प्लानर",
  "grid.reDesc": "क्या आप वो 2BHK सच में खरीद सकते हो?",
  "grid.reDetail": "EMI, डाउन पेमेंट, स्टैम्प ड्यूटी, और मेंटेनेंस — सब जोड़कर असली लागत देखो।",
  "grid.openTool": "टूल खोलें",
  "ba.title": "बदलाव देखो",
  "ba.sub": "VittaSathi आपकी फाइनेंस को कैसे ऑप्टिमाइज़ करता है — स्लाइड करो",
  "ba.before": "पहले",
  "ba.after": "बाद में",
  "ba.beforeLabel": "VittaSathi से पहले",
  "ba.afterLabel": "VittaSathi के बाद",
  "ba.b1": "❌ अलग-अलग रेट पर 3 रैंडम FD",
  "ba.b2": "❌ HRA छूट की कोई जानकारी नहीं",
  "ba.b3": "❌ होम लोन पर ₹8,400/माह ज़्यादा भुगतान",
  "ba.b4": "❌ गलत टैक्स रिजीम — ₹22K ज़्यादा दे रहे",
  "ba.b5": "❌ इमरजेंसी फंड की कोई रणनीति नहीं",
  "ba.a1": "✅ हाई-यील्ड डेट फंड में एकत्र",
  "ba.a2": "✅ HRA क्लेम — ₹12K/माह बचत",
  "ba.a3": "✅ प्रीपेमेंट प्लान से ₹6.2L ब्याज बचा",
  "ba.a4": "✅ सही रिजीम — ₹22K रिफंड फाइल",
  "ba.a5": "✅ 6 महीने का इमरजेंसी बफर लिक्विड फंड में",
  "hack.title": "💡 आज का हैक",
  "hack.tag": "टैक्स हैक #47",
  "hack.headline": "NPS से 80C के ऊपर अतिरिक्त ₹50,000 की कटौती मिलती है",
  "hack.body": "ज़्यादातर सैलरीड लोग 80C की ₹1.5L लिमिट तक पहुँचकर रुक जाते हैं। लेकिन 80CCD(1B) से NPS में ₹50,000 की अतिरिक्त कटौती मिलती है। 30% टैक्स ब्रैकेट में यह हर साल ₹15,600 की बचत है।",
  "hack.readMore": "और पढ़ें",
  "comp.title": "साइड-बाय-साइड तुलना",
  "comp.sub": "कौन सा प्रोडक्ट बेहतर है — डेटा देखो",
  "comp.rewards": "रिवॉर्ड्स",
  "comp.annualFee": "वार्षिक शुल्क",
  "comp.compareAll": "सब तुलना करें",
  "wl.title": "अपने वेल्थ लीक्स खोजो",
  "wl.sub": "छुपी हुई बचत खोजो जो आप छोड़ रहे हो",
  "wl.label": "अपनी मासिक सैलरी दर्ज करें",
  "wl.placeholder": "जैसे 75,000",
  "wl.hacking": "🔍 छुपे हुए हैक्स जो आप मिस कर रहे हो",
  "wl.hra": "HRA सही से क्लेम करके ₹{amount}/माह बचा सकते हो",
  "wl.nps": "NPS (80CCD) से ₹{amount}/साल अतिरिक्त टैक्स कटौती",
  "wl.fd": "FD ब्याज ~₹{amount}/साल टैक्सेबल है — डेट फंड्स में शिफ्ट करो",
  "wl.deepDive": "टैक्स प्लानर में गहराई से जानो",
  "nl.title": "मुफ़्त वेल्थ गाइड पाओ",
  "nl.sub": "टैक्स हैक्स, SIP स्ट्रैटेजी, और रियल-एस्टेट चेकलिस्ट के साथ 20 पेज की PDF। आपके इनबॉक्स में — कोई स्पैम नहीं।",
  "nl.placeholder": "your@email.com",
  "nl.cta": "PDF डाउनलोड करें",
  "nl.success": "🎉 इनबॉक्स चेक करो! गाइड आ रही है।",
  "footer.legal": "कानूनी अस्वीकरण",
  "footer.privacy": "गोपनीयता नीति",
  "footer.disclaimer": "© {year} VittaSathi. केवल सूचनात्मक उद्देश्यों के लिए। वित्तीय सलाह नहीं है। निवेश निर्णय लेने से पहले प्रमाणित वित्तीय सलाहकार से परामर्श करें।",
};

const bn: TranslationKeys = {
  "nav.tools": "টুলস",
  "nav.compare": "তুলনা",
  "nav.realEstate": "রিয়েল এস্টেট",
  "nav.taxCenter": "ট্যাক্স প্ল্যানার",
  "nav.dashboard": "ড্যাশবোর্ড",
  "nav.getStarted": "শুরু করুন",
  "hero.badge": "আধুনিক ভারতের জন্য স্মার্ট আর্থিক টুলস",
  "hero.headline1": "অনুমান করা বন্ধ করুন,",
  "hero.headline2": "বাড়তে শুরু করুন।",
  "hero.sub": "ঋণ শেষ করুন, ট্যাক্স বাঁচান, এবং আপনার স্বপ্নের বাড়ি পরিকল্পনা করুন।",
  "hero.cta1": "শুরু করুন",
  "hero.cta2": "টুলস দেখুন",
  "sip.title": "মিনি SIP ক্যালকুলেটর",
  "sip.monthly": "মাসিক SIP",
  "sip.period": "সময়কাল",
  "sip.yrs": "বছর",
  "sip.invested": "বিনিয়োগ",
  "sip.returns": "আনুমানিক রিটার্ন",
  "sip.total": "মোট",
  "sip.fullCalc": "সম্পূর্ণ ক্যালকুলেটর",
  "grid.title": "কুইক ফিক্স গ্রিড",
  "grid.sub": "তিনটি টুল। তিনটি অর্থ সমস্যার সমাধান।",
  "grid.debtTitle": "ঋণ-মুক্তি পথ",
  "grid.debtDesc": "সুদে লাখ টাকা বাঁচান।",
  "grid.debtDetail": "আপনি লোনে কতটা বেশি দিচ্ছেন তা জানুন এবং প্রিপেমেন্ট কৌশল তৈরি করুন।",
  "grid.taxTitle": "ট্যাক্স প্ল্যানার",
  "grid.taxDesc": "নতুন vs পুরানো রেজিম? ৩০ সেকেন্ডে জানুন।",
  "grid.taxDetail": "আপনার আয় ও কর্তন দিন, ইঞ্জিন সেরা রেজিম সুপারিশ করবে।",
  "grid.reTitle": "রিয়েল এস্টেট প্ল্যানার",
  "grid.reDesc": "আপনি কি সত্যিই সেই 2BHK কিনতে পারবেন?",
  "grid.reDetail": "EMI, ডাউন পেমেন্ট, স্ট্যাম্প ডিউটি এবং রক্ষণাবেক্ষণ — সব যোগ করে আসল খরচ দেখুন।",
  "grid.openTool": "টুল খুলুন",
  "ba.title": "রূপান্তর দেখুন",
  "ba.sub": "VittaSathi কীভাবে আপনার অর্থ অপ্টিমাইজ করে তা দেখুন",
  "ba.before": "আগে",
  "ba.after": "পরে",
  "ba.beforeLabel": "VittaSathi-র আগে",
  "ba.afterLabel": "VittaSathi-র পরে",
  "ba.b1": "❌ বিভিন্ন রেটে ৩টি এলোমেলো FD",
  "ba.b2": "❌ HRA ছাড় সম্পর্কে কোনো ধারণা নেই",
  "ba.b3": "❌ হোম লোনে ₹৮,৪০০/মাস বেশি দিচ্ছেন",
  "ba.b4": "❌ ভুল ট্যাক্স রেজিম — ₹২২K বেশি দিচ্ছেন",
  "ba.b5": "❌ জরুরি তহবিলের কোনো পরিকল্পনা নেই",
  "ba.a1": "✅ হাই-ইল্ড ডেট ফান্ডে একত্রিত",
  "ba.a2": "✅ HRA দাবি — ₹১২K/মাস সঞ্চয়",
  "ba.a3": "✅ প্রিপেমেন্ট প্ল্যানে ₹৬.২L সুদ বাঁচল",
  "ba.a4": "✅ সঠিক রেজিম — ₹২২K রিফান্ড ফাইল",
  "ba.a5": "✅ ৬ মাসের জরুরি বাফার লিকুইড ফান্ডে",
  "hack.title": "💡 আজকের হ্যাক",
  "hack.tag": "ট্যাক্স হ্যাক #৪৭",
  "hack.headline": "NPS থেকে 80C-র উপরে অতিরিক্ত ₹৫০,০০০ কর্তন পাবেন",
  "hack.body": "বেশিরভাগ চাকরিজীবী 80C-র ₹১.৫L সীমায় পৌঁছে থেমে যান। কিন্তু 80CCD(1B) NPS-এ অতিরিক্ত ₹৫০,০০০ কর্তন দেয়। ৩০% ট্যাক্স ব্র্যাকেটে এটি প্রতি বছর ₹১৫,৬০০ সঞ্চয়।",
  "hack.readMore": "আরও পড়ুন",
  "comp.title": "পাশাপাশি তুলনা",
  "comp.sub": "কোন প্রোডাক্ট ভালো — ডেটা দেখুন",
  "comp.rewards": "রিওয়ার্ডস",
  "comp.annualFee": "বার্ষিক ফি",
  "comp.compareAll": "সব তুলনা করুন",
  "wl.title": "আপনার ওয়েলথ লিক খুঁজুন",
  "wl.sub": "লুকানো সঞ্চয় আবিষ্কার করুন",
  "wl.label": "আপনার মাসিক বেতন দিন",
  "wl.placeholder": "যেমন ৭৫,০০০",
  "wl.hacking": "🔍 লুকানো হ্যাকস যা আপনি মিস করছেন",
  "wl.hra": "HRA সঠিকভাবে দাবি করে ₹{amount}/মাস বাঁচাতে পারেন",
  "wl.nps": "NPS (80CCD) থেকে ₹{amount}/বছর অতিরিক্ত ট্যাক্স কর্তন",
  "wl.fd": "FD সুদ ~₹{amount}/বছর করযোগ্য — ডেট ফান্ডে শিফট করুন",
  "wl.deepDive": "ট্যাক্স প্ল্যানারে গভীরে যান",
  "nl.title": "বিনামূল্যে ওয়েলথ গাইড পান",
  "nl.sub": "ট্যাক্স হ্যাক, SIP কৌশল এবং রিয়েল-এস্টেট চেকলিস্ট সহ ২০ পৃষ্ঠার PDF।",
  "nl.placeholder": "your@email.com",
  "nl.cta": "PDF ডাউনলোড",
  "nl.success": "🎉 ইনবক্স চেক করুন! গাইড আসছে।",
  "footer.legal": "আইনি দাবিত্যাগ",
  "footer.privacy": "গোপনীয়তা নীতি",
  "footer.disclaimer": "© {year} VittaSathi। শুধুমাত্র তথ্যমূলক উদ্দেশ্যে। আর্থিক পরামর্শ নয়।",
};

const te: TranslationKeys = {
  "nav.tools": "టూల్స్",
  "nav.compare": "పోలిక",
  "nav.realEstate": "రియల్ ఎస్టేట్",
  "nav.taxCenter": "ట్యాక్స్ ప్లానర్",
  "nav.dashboard": "డాష్‌బోర్డ్",
  "nav.getStarted": "ప్రారంభించండి",
  "hero.badge": "ఆధునిక భారతదేశానికి స్మార్ట్ ఆర్థిక టూల్స్",
  "hero.headline1": "అంచనా వేయడం ఆపండి,",
  "hero.headline2": "ఎదగడం మొదలు పెట్టండి.",
  "hero.sub": "అప్పు తీర్చండి, ట్యాక్స్ ఆదా చేయండి, మీ కలల ఇంటిని ప్లాన్ చేయండి.",
  "hero.cta1": "ప్రారంభించండి",
  "hero.cta2": "టూల్స్ చూడండి",
  "sip.title": "మినీ SIP కాలిక్యులేటర్",
  "sip.monthly": "నెలవారీ SIP",
  "sip.period": "కాల వ్యవధి",
  "sip.yrs": "సంవత్సరాలు",
  "sip.invested": "పెట్టుబడి",
  "sip.returns": "అంచనా రిటర్న్స్",
  "sip.total": "మొత్తం",
  "sip.fullCalc": "పూర్తి కాలిక్యులేటర్",
  "grid.title": "క్విక్ ఫిక్స్ గ్రిడ్",
  "grid.sub": "మూడు టూల్స్. మూడు డబ్బు సమస్యలు పరిష్కారం.",
  "grid.debtTitle": "అప్పు-విముక్తి",
  "grid.debtDesc": "వడ్డీలో లక్షలు ఆదా చేయండి.",
  "grid.debtDetail": "మీరు లోన్లపై ఎంత ఎక్కువ చెల్లిస్తున్నారో తెలుసుకోండి.",
  "grid.taxTitle": "ట్యాక్స్ ప్లానర్",
  "grid.taxDesc": "కొత్త vs పాత రెజీమ్? 30 సెకన్లలో తెలుసుకోండి.",
  "grid.taxDetail": "మీ ఆదాయం, తగ్గింపులు ఇవ్వండి, ఇంజిన్ ఉత్తమ రెజీమ్ సూచిస్తుంది.",
  "grid.reTitle": "రియల్ ఎస్టేట్ ప్లానర్",
  "grid.reDesc": "ఆ 2BHK నిజంగా కొనగలరా?",
  "grid.reDetail": "EMI, డౌన్ పేమెంట్, స్టాంప్ డ్యూటీ — అన్నీ కలిపి అసలు ఖర్చు చూడండి.",
  "grid.openTool": "టూల్ తెరవండి",
  "ba.title": "మార్పు చూడండి",
  "ba.sub": "VittaSathi మీ ఆర్థిక వ్యవహారాలను ఎలా ఆప్టిమైజ్ చేస్తుందో చూడండి",
  "ba.before": "ముందు",
  "ba.after": "తర్వాత",
  "ba.beforeLabel": "VittaSathi ముందు",
  "ba.afterLabel": "VittaSathi తర్వాత",
  "ba.b1": "❌ వేర్వేరు రేట్లలో 3 రాండమ్ FDలు",
  "ba.b2": "❌ HRA మినహాయింపు గురించి తెలియదు",
  "ba.b3": "❌ హోమ్ లోన్‌పై ₹8,400/నెల ఎక్కువ చెల్లింపు",
  "ba.b4": "❌ తప్పు ట్యాక్స్ రెజీమ్ — ₹22K ఎక్కువ",
  "ba.b5": "❌ ఎమర్జెన్సీ ఫండ్ వ్యూహం లేదు",
  "ba.a1": "✅ హై-యీల్డ్ డెట్ ఫండ్స్‌లో కలిపారు",
  "ba.a2": "✅ HRA క్లెయిమ్ — ₹12K/నెల ఆదా",
  "ba.a3": "✅ ప్రీపేమెంట్ ప్లాన్‌తో ₹6.2L వడ్డీ ఆదా",
  "ba.a4": "✅ సరైన రెజీమ్ — ₹22K రీఫండ్",
  "ba.a5": "✅ 6 నెలల ఎమర్జెన్సీ బఫర్ లిక్విడ్ ఫండ్‌లో",
  "hack.title": "💡 ఈ రోజు హ్యాక్",
  "hack.tag": "ట్యాక్స్ హ్యాక్ #47",
  "hack.headline": "NPS ద్వారా 80C కంటే ₹50,000 అదనపు తగ్గింపు",
  "hack.body": "చాలా మంది 80C ₹1.5L వద్ద ఆగిపోతారు. 80CCD(1B) NPS కోసం ₹50,000 అదనపు తగ్గింపు ఇస్తుంది. 30% ట్యాక్స్ బ్రాకెట్‌లో ఏటా ₹15,600 ఆదా.",
  "hack.readMore": "మరింత చదవండి",
  "comp.title": "పక్కపక్కన పోలిక",
  "comp.sub": "ఏ ఉత్పత్తి మెరుగు — డేటా చూడండి",
  "comp.rewards": "రివార్డ్స్",
  "comp.annualFee": "వార్షిక ఫీజు",
  "comp.compareAll": "అన్నీ పోల్చండి",
  "wl.title": "మీ వెల్త్ లీక్స్ కనుగొనండి",
  "wl.sub": "మీరు వదిలేస్తున్న దాచిన ఆదా కనుగొనండి",
  "wl.label": "మీ నెలవారీ జీతం నమోదు చేయండి",
  "wl.placeholder": "ఉదా. 75,000",
  "wl.hacking": "🔍 మీరు మిస్ అవుతున్న హిడెన్ హ్యాక్స్",
  "wl.hra": "HRA సరిగ్గా క్లెయిమ్ చేసి ₹{amount}/నెల ఆదా చేయవచ్చు",
  "wl.nps": "NPS (80CCD) ద్వారా ₹{amount}/సంవత్సరం అదనపు ట్యాక్స్ తగ్గింపు",
  "wl.fd": "FD వడ్డీ ~₹{amount}/సంవత్సరం పన్ను పరిధిలోకి వస్తుంది — డెట్ ఫండ్స్‌కు మారండి",
  "wl.deepDive": "ట్యాక్స్ ప్లానర్‌లో లోతుగా చూడండి",
  "nl.title": "ఉచిత వెల్త్ గైడ్ పొందండి",
  "nl.sub": "ట్యాక్స్ హ్యాక్స్, SIP వ్యూహాలు, రియల్-ఎస్టేట్ చెక్‌లిస్ట్‌లతో 20 పేజీల PDF.",
  "nl.placeholder": "your@email.com",
  "nl.cta": "PDF డౌన్‌లోడ్",
  "nl.success": "🎉 ఇన్‌బాక్స్ చెక్ చేయండి! గైడ్ వస్తోంది.",
  "footer.legal": "చట్టపరమైన నిరాకరణలు",
  "footer.privacy": "గోప్యతా విధానం",
  "footer.disclaimer": "© {year} VittaSathi. సమాచార ప్రయోజనాల కోసం మాత్రమే. ఆర్థిక సలహా కాదు.",
};

const mr: TranslationKeys = {
  "nav.tools": "टूल्स",
  "nav.compare": "तुलना",
  "nav.realEstate": "रिअल इस्टेट",
  "nav.taxCenter": "टॅक्स प्लानर",
  "nav.dashboard": "डॅशबोर्ड",
  "nav.getStarted": "सुरू करा",
  "hero.badge": "आधुनिक भारतासाठी स्मार्ट आर्थिक साधने",
  "hero.headline1": "अंदाज लावणे थांबवा,",
  "hero.headline2": "वाढायला सुरुवात करा.",
  "hero.sub": "कर्ज संपवा, टॅक्स वाचवा, आणि स्वप्नातल्या घराचे नियोजन करा.",
  "hero.cta1": "सुरू करा",
  "hero.cta2": "टूल्स पहा",
  "sip.title": "मिनी SIP कॅल्क्युलेटर",
  "sip.monthly": "मासिक SIP",
  "sip.period": "कालावधी",
  "sip.yrs": "वर्षे",
  "sip.invested": "गुंतवणूक",
  "sip.returns": "अंदाजित परतावा",
  "sip.total": "एकूण",
  "sip.fullCalc": "संपूर्ण कॅल्क्युलेटर",
  "grid.title": "क्विक फिक्स ग्रिड",
  "grid.sub": "तीन साधने. तीन पैशांच्या समस्या सोडवल्या.",
  "grid.debtTitle": "कर्ज-मुक्ती",
  "grid.debtDesc": "व्याजात लाखो वाचवा.",
  "grid.debtDetail": "लोनवर किती जास्त भरत आहात ते शोधा आणि प्रीपेमेंट स्ट्रॅटेजी बनवा.",
  "grid.taxTitle": "टॅक्स प्लानर",
  "grid.taxDesc": "नवी vs जुनी रिजीम? ३० सेकंदात जाणा.",
  "grid.taxDetail": "तुमचे उत्पन्न आणि कपात टाका, इंजिन सर्वोत्तम रिजीम सुचवेल.",
  "grid.reTitle": "रिअल इस्टेट प्लॅनर",
  "grid.reDesc": "ती 2BHK खरोखर घेऊ शकता का?",
  "grid.reDetail": "EMI, डाउन पेमेंट, स्टॅम्प ड्यूटी — सगळे जोडून खरा खर्च पहा.",
  "grid.openTool": "टूल उघडा",
  "ba.title": "बदल पहा",
  "ba.sub": "VittaSathi तुमचे आर्थिक व्यवस्थापन कसे सुधारतो ते पहा",
  "ba.before": "आधी",
  "ba.after": "नंतर",
  "ba.beforeLabel": "VittaSathi आधी",
  "ba.afterLabel": "VittaSathi नंतर",
  "ba.b1": "❌ वेगवेगळ्या दरांवर ३ रँडम FD",
  "ba.b2": "❌ HRA सवलतीची कल्पना नाही",
  "ba.b3": "❌ होम लोनवर ₹८,४००/महिना जास्त भरत आहात",
  "ba.b4": "❌ चुकीची टॅक्स रिजीम — ₹२२K जास्त",
  "ba.b5": "❌ इमर्जन्सी फंड स्ट्रॅटेजी नाही",
  "ba.a1": "✅ हाय-यील्ड डेट फंडात एकत्र",
  "ba.a2": "✅ HRA क्लेम — ₹१२K/महिना बचत",
  "ba.a3": "✅ प्रीपेमेंट प्लॅनने ₹६.२L व्याज वाचले",
  "ba.a4": "✅ योग्य रिजीम — ₹२२K रिफंड",
  "ba.a5": "✅ ६ महिन्यांचा इमर्जन्सी बफर लिक्विड फंडात",
  "hack.title": "💡 आजचा हॅक",
  "hack.tag": "टॅक्स हॅक #४७",
  "hack.headline": "NPS मधून 80C च्या वर ₹५०,००० अतिरिक्त कपात मिळते",
  "hack.body": "बहुतेक पगारदार 80C ₹१.५L मर्यादेवर थांबतात. पण 80CCD(1B) NPS साठी ₹५०,००० अतिरिक्त कपात देतो. ३०% टॅक्स ब्रॅकेटमध्ये दरवर्षी ₹१५,६०० बचत.",
  "hack.readMore": "अधिक वाचा",
  "comp.title": "शेजारी-शेजारी तुलना",
  "comp.sub": "कोणते प्रॉडक्ट चांगले — डेटा पहा",
  "comp.rewards": "रिवॉर्ड्स",
  "comp.annualFee": "वार्षिक शुल्क",
  "comp.compareAll": "सर्व तुलना करा",
  "wl.title": "तुमचे वेल्थ लीक शोधा",
  "wl.sub": "लपलेली बचत शोधा",
  "wl.label": "तुमचा मासिक पगार टाका",
  "wl.placeholder": "उदा. ७५,०००",
  "wl.hacking": "🔍 तुम्ही गमावत असलेले लपलेले हॅक्स",
  "wl.hra": "HRA योग्यरित्या क्लेम करून ₹{amount}/महिना वाचवू शकता",
  "wl.nps": "NPS (80CCD) मधून ₹{amount}/वर्ष अतिरिक्त टॅक्स कपात",
  "wl.fd": "FD व्याज ~₹{amount}/वर्ष करपात्र — डेट फंडात शिफ्ट करा",
  "wl.deepDive": "टॅक्स प्लानरमध्ये सखोल जाणा",
  "nl.title": "मोफत वेल्थ गाइड मिळवा",
  "nl.sub": "टॅक्स हॅक्स, SIP स्ट्रॅटेजी, रिअल-इस्टेट चेकलिस्ट — २० पानांची PDF.",
  "nl.placeholder": "your@email.com",
  "nl.cta": "PDF डाउनलोड",
  "nl.success": "🎉 इनबॉक्स तपासा! गाइड येत आहे.",
  "footer.legal": "कायदेशीर अस्वीकरण",
  "footer.privacy": "गोपनीयता धोरण",
  "footer.disclaimer": "© {year} VittaSathi. केवळ माहितीच्या उद्देशाने. आर्थिक सल्ला नाही.",
};

const ta: TranslationKeys = {
  "nav.tools": "கருவிகள்",
  "nav.compare": "ஒப்பிடுக",
  "nav.realEstate": "ரியல் எஸ்டேட்",
  "nav.taxCenter": "வரி திட்டமிடுபவர்",
  "nav.dashboard": "டாஷ்போர்ட்",
  "nav.getStarted": "தொடங்கு",
  "hero.badge": "நவீன இந்தியாவிற்கான ஸ்மார்ட் நிதி கருவிகள்",
  "hero.headline1": "யூகிப்பதை நிறுத்துங்கள்,",
  "hero.headline2": "வளர ஆரம்பியுங்கள்.",
  "hero.sub": "கடனை அழிக்கவும், வரி சேமிக்கவும், கனவு வீட்டை திட்டமிடவும்.",
  "hero.cta1": "தொடங்கு",
  "hero.cta2": "கருவிகள் பார்",
  "sip.title": "மினி SIP கால்குலேட்டர்",
  "sip.monthly": "மாதாந்திர SIP",
  "sip.period": "கால அளவு",
  "sip.yrs": "ஆண்டுகள்",
  "sip.invested": "முதலீடு",
  "sip.returns": "மதிப்பிட்ட வருமானம்",
  "sip.total": "மொத்தம்",
  "sip.fullCalc": "முழு கால்குலேட்டர்",
  "grid.title": "குவிக் ஃபிக்ஸ் கிரிட்",
  "grid.sub": "மூன்று கருவிகள். மூன்று பண பிரச்சனைகள் தீர்வு.",
  "grid.debtTitle": "கடன்-விடுதலை",
  "grid.debtDesc": "வட்டியில் லட்சங்கள் சேமியுங்கள்.",
  "grid.debtDetail": "கடனில் எவ்வளவு அதிகம் செலுத்துகிறீர்கள் என்று கண்டறியுங்கள்.",
  "grid.taxTitle": "வரி திட்டமிடுபவர்",
  "grid.taxDesc": "புதிய vs பழைய முறை? 30 வினாடியில் தெரிந்துகொள்.",
  "grid.taxDetail": "உங்கள் வருமானம், கழிவுகளை உள்ளிடுங்கள்.",
  "grid.reTitle": "ரியல் எஸ்டேட் பிளானர்",
  "grid.reDesc": "அந்த 2BHK-ஐ வாங்க முடியுமா?",
  "grid.reDetail": "EMI, டவுன் பேமெண்ட், ஸ்டாம்ப் டூட்டி — எல்லாம் சேர்த்து உண்மையான செலவு பாருங்கள்.",
  "grid.openTool": "கருவி திற",
  "ba.title": "மாற்றத்தை பாருங்கள்",
  "ba.sub": "VittaSathi உங்கள் நிதியை எப்படி மேம்படுத்துகிறது என்று பாருங்கள்",
  "ba.before": "முன்",
  "ba.after": "பின்",
  "ba.beforeLabel": "VittaSathi முன்",
  "ba.afterLabel": "VittaSathi பின்",
  "ba.b1": "❌ வெவ்வேறு விகிதங்களில் 3 ரேண்டம் FD",
  "ba.b2": "❌ HRA விலக்கு பற்றி தெரியாது",
  "ba.b3": "❌ வீட்டு கடனில் ₹8,400/மாதம் அதிகம்",
  "ba.b4": "❌ தவறான வரி முறை — ₹22K அதிகம்",
  "ba.b5": "❌ அவசர நிதி திட்டம் இல்லை",
  "ba.a1": "✅ அதிக வருமான கடன் நிதிகளில் ஒருங்கிணைப்பு",
  "ba.a2": "✅ HRA கோரிக்கை — ₹12K/மாதம் சேமிப்பு",
  "ba.a3": "✅ முன்பணம் திட்டம் ₹6.2L வட்டி சேமிப்பு",
  "ba.a4": "✅ சரியான முறை — ₹22K திருப்பி",
  "ba.a5": "✅ 6 மாத அவசர இடையகம் லிக்விட் ஃபண்டில்",
  "hack.title": "💡 இன்றைய ஹேக்",
  "hack.tag": "வரி ஹேக் #47",
  "hack.headline": "NPS மூலம் 80C-க்கு மேல் ₹50,000 கூடுதல் கழிவு",
  "hack.body": "பெரும்பாலான சம்பளதாரர்கள் 80C ₹1.5L வரம்பில் நிற்கிறார்கள். 80CCD(1B) NPS-க்கு ₹50,000 கூடுதல் கழிவு. 30% வரி பிரிவில் ஆண்டுக்கு ₹15,600 சேமிப்பு.",
  "hack.readMore": "மேலும் படிக்க",
  "comp.title": "பக்கம்-பக்கம் ஒப்பீடு",
  "comp.sub": "எந்த தயாரிப்பு சிறந்தது — தரவு பாருங்கள்",
  "comp.rewards": "ரிவார்ட்ஸ்",
  "comp.annualFee": "ஆண்டு கட்டணம்",
  "comp.compareAll": "எல்லாம் ஒப்பிடு",
  "wl.title": "உங்கள் செல்வ கசிவுகளை கண்டறியுங்கள்",
  "wl.sub": "மறைந்திருக்கும் சேமிப்பை கண்டறியுங்கள்",
  "wl.label": "உங்கள் மாத சம்பளம் உள்ளிடுங்கள்",
  "wl.placeholder": "எ.கா. 75,000",
  "wl.hacking": "🔍 நீங்கள் தவறவிடும் மறைந்த ஹேக்குகள்",
  "wl.hra": "HRA சரியாக கோரி ₹{amount}/மாதம் சேமிக்கலாம்",
  "wl.nps": "NPS (80CCD) மூலம் ₹{amount}/ஆண்டு கூடுதல் வரி கழிவு",
  "wl.fd": "FD வட்டி ~₹{amount}/ஆண்டு வரிக்குட்பட்டது — கடன் நிதிகளுக்கு மாறுங்கள்",
  "wl.deepDive": "வரி ஆப்டிமைசரில் ஆழமாக பாருங்கள்",
  "nl.title": "இலவச செல்வ வழிகாட்டி பெறுங்கள்",
  "nl.sub": "வரி ஹேக்குகள், SIP உத்திகள், ரியல்-எஸ்டேட் சரிபார்ப்பு பட்டியல் — 20 பக்க PDF.",
  "nl.placeholder": "your@email.com",
  "nl.cta": "PDF பதிவிறக்கம்",
  "nl.success": "🎉 இன்பாக்ஸ் பாருங்கள்! வழிகாட்டி வருகிறது.",
  "footer.legal": "சட்ட மறுப்புகள்",
  "footer.privacy": "தனியுரிமை கொள்கை",
  "footer.disclaimer": "© {year} VittaSathi. தகவல் நோக்கங்களுக்கு மட்டும். நிதி ஆலோசனை அல்ல.",
};

const gu: TranslationKeys = {
  "nav.tools": "ટૂલ્સ",
  "nav.compare": "સરખામણી",
  "nav.realEstate": "રિયલ એસ્ટેટ",
  "nav.taxCenter": "ટેક્સ પ્લાનર",
  "nav.dashboard": "ડૅશબોર્ડ",
  "nav.getStarted": "શરૂ કરો",
  "hero.badge": "આધુનિક ભારત માટે સ્માર્ટ નાણાકીય ટૂલ્સ",
  "hero.headline1": "અંદાજો લગાવવાનું બંધ કરો,",
  "hero.headline2": "વધવાનું શરૂ કરો.",
  "hero.sub": "દેવું ખતમ કરો, ટેક્સ બચાવો, અને તમારા સપનાના ઘરનું આયોજન કરો. આધુનિક ભારતીય કમાણી કરનારાઓ માટે બનાવેલું.",
  "hero.cta1": "શરૂ કરો",
  "hero.cta2": "ટૂલ્સ જુઓ",
  "sip.title": "મિની SIP કેલ્ક્યુલેટર",
  "sip.monthly": "માસિક SIP",
  "sip.period": "સમયગાળો",
  "sip.yrs": "વર્ષ",
  "sip.invested": "રોકાણ",
  "sip.returns": "અંદાજિત વળતર",
  "sip.total": "કુલ",
  "sip.fullCalc": "સંપૂર્ણ કેલ્ક્યુલેટર",
  "grid.title": "ક્વિક ફિક્સ ગ્રિડ",
  "grid.sub": "ત્રણ ટૂલ્સ. ત્રણ પૈસાની સમસ્યાઓ ઉકેલાઈ.",
  "grid.debtTitle": "દેવું-મુક્તિ",
  "grid.debtDesc": "વ્યાજમાં લાખો બચાવો.",
  "grid.debtDetail": "તમે લોન પર કેટલું વધારે ચૂકવો છો તે જાણો અને પ્રીપેમેન્ટ વ્યૂહરચના બનાવો.",
  "grid.taxTitle": "ટેક્સ પ્લાનર",
  "grid.taxDesc": "નવી vs જૂની રિજીમ? 30 સેકન્ડમાં જાણો.",
  "grid.taxDetail": "તમારી આવક અને કપાત દાખલ કરો, એન્જિન શ્રેષ્ઠ રિજીમ સૂચવશે.",
  "grid.reTitle": "રિયલ એસ્ટેટ પ્લાનર",
  "grid.reDesc": "શું તમે ખરેખર તે 2BHK ખરીદી શકો છો?",
  "grid.reDetail": "EMI, ડાઉન પેમેન્ટ, સ્ટેમ્પ ડ્યુટી — બધું ઉમેરીને ખરો ખર્ચ જુઓ.",
  "grid.openTool": "ટૂલ ખોલો",
  "ba.title": "પરિવર્તન જુઓ",
  "ba.sub": "VittaSathi તમારા નાણાંને કેવી રીતે ઓપ્ટિમાઇઝ કરે છે તે જુઓ",
  "ba.before": "પહેલાં",
  "ba.after": "પછી",
  "ba.beforeLabel": "VittaSathi પહેલાં",
  "ba.afterLabel": "VittaSathi પછી",
  "ba.b1": "❌ જુદા જુદા દરે 3 રેન્ડમ FD",
  "ba.b2": "❌ HRA છૂટ વિશે કોઈ ખ્યાલ નથી",
  "ba.b3": "❌ હોમ લોન પર ₹8,400/મહિનો વધારે ચૂકવણી",
  "ba.b4": "❌ ખોટી ટેક્સ રિજીમ — ₹22K વધારે",
  "ba.b5": "❌ ઇમર્જન્સી ફંડ વ્યૂહરચના નથી",
  "ba.a1": "✅ હાઈ-યીલ્ડ ડેટ ફંડમાં એકત્રિત",
  "ba.a2": "✅ HRA ક્લેમ — ₹12K/મહિનો બચત",
  "ba.a3": "✅ પ્રીપેમેન્ટ પ્લાનથી ₹6.2L વ્યાજ બચ્યું",
  "ba.a4": "✅ યોગ્ય રિજીમ — ₹22K રિફંડ",
  "ba.a5": "✅ 6 મહિનાનો ઇમર્જન્સી બફર લિક્વિડ ફંડમાં",
  "hack.title": "💡 આજનો હેક",
  "hack.tag": "ટેક્સ હેક #47",
  "hack.headline": "NPS થી 80C ઉપરાંત ₹50,000 વધારાની કપાત મળે છે",
  "hack.body": "મોટાભાગના પગારદારો 80C ₹1.5L મર્યાદા પર અટકી જાય છે. પરંતુ 80CCD(1B) NPS માટે ₹50,000 વધારાની કપાત આપે છે. 30% ટેક્સ બ્રેકેટમાં દર વર્ષે ₹15,600 બચત.",
  "hack.readMore": "વધુ વાંચો",
  "comp.title": "બાજુ-બાજુ સરખામણી",
  "comp.sub": "કયું પ્રોડક્ટ સારું છે — ડેટા જુઓ",
  "comp.rewards": "રિવોર્ડ્સ",
  "comp.annualFee": "વાર્ષિક ફી",
  "comp.compareAll": "બધાની સરખામણી કરો",
  "wl.title": "તમારા વેલ્થ લીક્સ શોધો",
  "wl.sub": "છુપાયેલી બચત શોધો જે તમે ચૂકી રહ્યા છો",
  "wl.label": "તમારો માસિક પગાર દાખલ કરો",
  "wl.placeholder": "દા.ત. 75,000",
  "wl.hacking": "🔍 છુપાયેલા હેક્સ જે તમે ચૂકી રહ્યા છો",
  "wl.hra": "HRA યોગ્ય રીતે ક્લેમ કરીને ₹{amount}/મહિનો બચાવી શકો છો",
  "wl.nps": "NPS (80CCD) થી ₹{amount}/વર્ષ વધારાની ટેક્સ કપાત",
  "wl.fd": "FD વ્યાજ ~₹{amount}/વર્ષ કરપાત્ર છે — ડેટ ફંડ્સમાં શિફ્ટ કરો",
  "wl.deepDive": "ટેક્સ પ્લાનરમાં ઊંડાણથી જાણો",
  "nl.title": "મફત વેલ્થ ગાઈડ મેળવો",
  "nl.sub": "ટેક્સ હેક્સ, SIP વ્યૂહરચના, રિયલ-એસ્ટેટ ચેકલિસ્ટ — 20 પાનાની PDF.",
  "nl.placeholder": "your@email.com",
  "nl.cta": "PDF ડાઉનલોડ",
  "nl.success": "🎉 ઇનબોક્સ ચેક કરો! ગાઈડ આવી રહી છે.",
  "footer.legal": "કાનૂની અસ્વીકરણ",
  "footer.privacy": "ગોપનીયતા નીતિ",
  "footer.disclaimer": "© {year} VittaSathi. ફક્ત માહિતીના હેતુ માટે. નાણાકીય સલાહ નથી.",
};

const kn: TranslationKeys = {
  "nav.tools": "ಟೂಲ್ಸ್",
  "nav.compare": "ಹೋಲಿಕೆ",
  "nav.realEstate": "ರಿಯಲ್ ಎಸ್ಟೇಟ್",
  "nav.taxCenter": "ತೆರಿಗೆ ಯೋಜಕ",
  "nav.dashboard": "ಡ್ಯಾಶ್‌ಬೋರ್ಡ್",
  "nav.getStarted": "ಪ್ರಾರಂಭಿಸಿ",
  "hero.badge": "ಆಧುನಿಕ ಭಾರತಕ್ಕಾಗಿ ಸ್ಮಾರ್ಟ್ ಹಣಕಾಸು ಟೂಲ್ಸ್",
  "hero.headline1": "ಊಹಿಸುವುದನ್ನು ನಿಲ್ಲಿಸಿ,",
  "hero.headline2": "ಬೆಳೆಯಲು ಶುರು ಮಾಡಿ.",
  "hero.sub": "ಸಾಲ ತೀರಿಸಿ, ತೆರಿಗೆ ಉಳಿಸಿ, ಮತ್ತು ನಿಮ್ಮ ಕನಸಿನ ಮನೆ ಯೋಜಿಸಿ. ಆಧುನಿಕ ಭಾರತೀಯ ಗಳಿಕೆದಾರರಿಗಾಗಿ ನಿರ್ಮಿಸಲಾಗಿದೆ.",
  "hero.cta1": "ಪ್ರಾರಂಭಿಸಿ",
  "hero.cta2": "ಟೂಲ್ಸ್ ನೋಡಿ",
  "sip.title": "ಮಿನಿ SIP ಕ್ಯಾಲ್ಕುಲೇಟರ್",
  "sip.monthly": "ಮಾಸಿಕ SIP",
  "sip.period": "ಸಮಯಾವಧಿ",
  "sip.yrs": "ವರ್ಷಗಳು",
  "sip.invested": "ಹೂಡಿಕೆ",
  "sip.returns": "ಅಂದಾಜು ಲಾಭ",
  "sip.total": "ಒಟ್ಟು",
  "sip.fullCalc": "ಪೂರ್ಣ ಕ್ಯಾಲ್ಕುಲೇಟರ್",
  "grid.title": "ಕ್ವಿಕ್ ಫಿಕ್ಸ್ ಗ್ರಿಡ್",
  "grid.sub": "ಮೂರು ಟೂಲ್ಸ್. ಮೂರು ಹಣದ ಸಮಸ್ಯೆಗಳು ಪರಿಹಾರ.",
  "grid.debtTitle": "ಸಾಲದಿಂದ ಮುಕ್ತಿ",
  "grid.debtDesc": "ಬಡ್ಡಿಯಲ್ಲಿ ಲಕ್ಷಗಳನ್ನು ಉಳಿಸಿ.",
  "grid.debtDetail": "ಸಾಲಗಳಲ್ಲಿ ಎಷ್ಟು ಹೆಚ್ಚು ಪಾವತಿಸುತ್ತಿದ್ದೀರಿ ಎಂದು ತಿಳಿಯಿರಿ ಮತ್ತು ಪ್ರಿಪೇಮೆಂಟ್ ತಂತ್ರ ರಚಿಸಿ.",
  "grid.taxTitle": "ತೆರಿಗೆ ಯೋಜಕ",
  "grid.taxDesc": "ಹೊಸ vs ಹಳೆಯ ರೆಜೀಮ್? 30 ಸೆಕೆಂಡ್‌ನಲ್ಲಿ ತಿಳಿಯಿರಿ.",
  "grid.taxDetail": "ನಿಮ್ಮ ಆದಾಯ ಮತ್ತು ಕಡಿತಗಳನ್ನು ನಮೂದಿಸಿ, ಎಂಜಿನ್ ಅತ್ಯುತ್ತಮ ರೆಜೀಮ್ ಶಿಫಾರಸು ಮಾಡುತ್ತದೆ.",
  "grid.reTitle": "ರಿಯಲ್ ಎಸ್ಟೇಟ್ ಪ್ಲ್ಯಾನರ್",
  "grid.reDesc": "ಆ 2BHK ನಿಜವಾಗಿಯೂ ಕೊಳ್ಳಬಹುದಾ?",
  "grid.reDetail": "EMI, ಡೌನ್ ಪೇಮೆಂಟ್, ಸ್ಟ್ಯಾಂಪ್ ಡ್ಯೂಟಿ — ಎಲ್ಲಾ ಸೇರಿಸಿ ನಿಜವಾದ ವೆಚ್ಚ ನೋಡಿ.",
  "grid.openTool": "ಟೂಲ್ ತೆರೆಯಿರಿ",
  "ba.title": "ರೂಪಾಂತರ ನೋಡಿ",
  "ba.sub": "VittaSathi ನಿಮ್ಮ ಹಣಕಾಸನ್ನು ಹೇಗೆ ಆಪ್ಟಿಮೈಜ್ ಮಾಡುತ್ತದೆ ಎಂದು ನೋಡಿ",
  "ba.before": "ಮೊದಲು",
  "ba.after": "ನಂತರ",
  "ba.beforeLabel": "VittaSathi ಮೊದಲು",
  "ba.afterLabel": "VittaSathi ನಂತರ",
  "ba.b1": "❌ ಬೇರೆ ಬೇರೆ ದರಗಳಲ್ಲಿ 3 ರ‍್ಯಾಂಡಮ್ FD",
  "ba.b2": "❌ HRA ವಿನಾಯಿತಿ ಬಗ್ಗೆ ಗೊತ್ತಿಲ್ಲ",
  "ba.b3": "❌ ಹೋಮ್ ಲೋನ್‌ನಲ್ಲಿ ₹8,400/ತಿಂಗಳು ಹೆಚ್ಚು ಪಾವತಿ",
  "ba.b4": "❌ ತಪ್ಪು ತೆರಿಗೆ ರೆಜೀಮ್ — ₹22K ಹೆಚ್ಚು",
  "ba.b5": "❌ ತುರ್ತು ನಿಧಿ ತಂತ್ರ ಇಲ್ಲ",
  "ba.a1": "✅ ಹೈ-ಯೀಲ್ಡ್ ಡೆಟ್ ಫಂಡ್‌ಗಳಲ್ಲಿ ಕ್ರೋಢೀಕರಿಸಲಾಗಿದೆ",
  "ba.a2": "✅ HRA ಕ್ಲೇಮ್ — ₹12K/ತಿಂಗಳು ಉಳಿತಾಯ",
  "ba.a3": "✅ ಪ್ರಿಪೇಮೆಂಟ್ ಪ್ಲ್ಯಾನ್‌ನಿಂದ ₹6.2L ಬಡ್ಡಿ ಉಳಿಯಿತು",
  "ba.a4": "✅ ಸರಿಯಾದ ರೆಜೀಮ್ — ₹22K ರಿಫಂಡ್",
  "ba.a5": "✅ 6 ತಿಂಗಳ ತುರ್ತು ಬಫರ್ ಲಿಕ್ವಿಡ್ ಫಂಡ್‌ನಲ್ಲಿ",
  "hack.title": "💡 ಇಂದಿನ ಹ್ಯಾಕ್",
  "hack.tag": "ತೆರಿಗೆ ಹ್ಯಾಕ್ #47",
  "hack.headline": "NPS ನಿಂದ 80C ಮೇಲೆ ₹50,000 ಹೆಚ್ಚುವರಿ ಕಡಿತ ಸಿಗುತ್ತದೆ",
  "hack.body": "ಹೆಚ್ಚಿನ ಸಂಬಳದಾರರು 80C ₹1.5L ಮಿತಿಯಲ್ಲಿ ನಿಲ್ಲುತ್ತಾರೆ. ಆದರೆ 80CCD(1B) NPS ಗಾಗಿ ₹50,000 ಹೆಚ್ಚುವರಿ ಕಡಿತ ನೀಡುತ್ತದೆ. 30% ತೆರಿಗೆ ಬ್ರಾಕೆಟ್‌ನಲ್ಲಿ ಪ್ರತಿ ವರ್ಷ ₹15,600 ಉಳಿತಾಯ.",
  "hack.readMore": "ಇನ್ನಷ್ಟು ಓದಿ",
  "comp.title": "ಪಕ್ಕ-ಪಕ್ಕ ಹೋಲಿಕೆ",
  "comp.sub": "ಯಾವ ಉತ್ಪನ್ನ ಉತ್ತಮ — ಡೇಟಾ ನೋಡಿ",
  "comp.rewards": "ರಿವಾರ್ಡ್ಸ್",
  "comp.annualFee": "ವಾರ್ಷಿಕ ಶುಲ್ಕ",
  "comp.compareAll": "ಎಲ್ಲಾ ಹೋಲಿಸಿ",
  "wl.title": "ನಿಮ್ಮ ಸಂಪತ್ತಿನ ಸೋರಿಕೆ ಹುಡುಕಿ",
  "wl.sub": "ನೀವು ಬಿಡುತ್ತಿರುವ ಮರೆಯಾದ ಉಳಿತಾಯ ಕಂಡುಹಿಡಿಯಿರಿ",
  "wl.label": "ನಿಮ್ಮ ಮಾಸಿಕ ಸಂಬಳ ನಮೂದಿಸಿ",
  "wl.placeholder": "ಉದಾ. 75,000",
  "wl.hacking": "🔍 ನೀವು ಕಳೆದುಕೊಳ್ಳುತ್ತಿರುವ ಅಡಗಿರುವ ಹ್ಯಾಕ್ಸ್",
  "wl.hra": "HRA ಸರಿಯಾಗಿ ಕ್ಲೇಮ್ ಮಾಡಿ ₹{amount}/ತಿಂಗಳು ಉಳಿಸಬಹುದು",
  "wl.nps": "NPS (80CCD) ಮೂಲಕ ₹{amount}/ವರ್ಷ ಹೆಚ್ಚುವರಿ ತೆರಿಗೆ ಕಡಿತ",
  "wl.fd": "FD ಬಡ್ಡಿ ~₹{amount}/ವರ್ಷ ತೆರಿಗೆಗೊಳಪಡುತ್ತದೆ — ಡೆಟ್ ಫಂಡ್‌ಗಳಿಗೆ ಬದಲಿಸಿ",
  "wl.deepDive": "ತೆರಿಗೆ ಯೋಜಕ‌ನಲ್ಲಿ ಆಳವಾಗಿ ನೋಡಿ",
  "nl.title": "ಉಚಿತ ಸಂಪತ್ತು ಗೈಡ್ ಪಡೆಯಿರಿ",
  "nl.sub": "ತೆರಿಗೆ ಹ್ಯಾಕ್ಸ್, SIP ತಂತ್ರಗಳು, ರಿಯಲ್-ಎಸ್ಟೇಟ್ ಚೆಕ್‌ಲಿಸ್ಟ್ — 20 ಪುಟಗಳ PDF.",
  "nl.placeholder": "your@email.com",
  "nl.cta": "PDF ಡೌನ್‌ಲೋಡ್",
  "nl.success": "🎉 ಇನ್‌ಬಾಕ್ಸ್ ಪರಿಶೀಲಿಸಿ! ಗೈಡ್ ಬರುತ್ತಿದೆ.",
  "footer.legal": "ಕಾನೂನು ನಿರಾಕರಣೆಗಳು",
  "footer.privacy": "ಗೌಪ್ಯತಾ ನೀತಿ",
  "footer.disclaimer": "© {year} VittaSathi. ಮಾಹಿತಿ ಉದ್ದೇಶಗಳಿಗಾಗಿ ಮಾತ್ರ. ಹಣಕಾಸು ಸಲಹೆ ಅಲ್ಲ.",
};

const ml: TranslationKeys = {
  "nav.tools": "ടൂളുകൾ",
  "nav.compare": "താരതമ്യം",
  "nav.realEstate": "റിയൽ എസ്റ്റേറ്റ്",
  "nav.taxCenter": "നികുതി ആസൂത്രകൻ",
  "nav.dashboard": "ഡാഷ്‌ബോർഡ്",
  "nav.getStarted": "ആരംഭിക്കൂ",
  "hero.badge": "ആധുനിക ഇന്ത്യയ്ക്കായുള്ള സ്മാർട്ട് ധനകാര്യ ടൂളുകൾ",
  "hero.headline1": "ഊഹിക്കുന്നത് നിർത്തൂ,",
  "hero.headline2": "വളരാൻ തുടങ്ങൂ.",
  "hero.sub": "കടം തീർക്കൂ, നികുതി ലാഭിക്കൂ, സ്വപ്ന ഭവനം ആസൂത്രണം ചെയ്യൂ. ആധുനിക ഇന്ത്യൻ വരുമാനക്കാർക്കായി നിർമ്മിച്ചത്.",
  "hero.cta1": "ആരംഭിക്കൂ",
  "hero.cta2": "ടൂളുകൾ കാണൂ",
  "sip.title": "മിനി SIP കാൽക്കുലേറ്റർ",
  "sip.monthly": "മാസ SIP",
  "sip.period": "കാലയളവ്",
  "sip.yrs": "വർഷം",
  "sip.invested": "നിക്ഷേപം",
  "sip.returns": "കണക്കാക്കിയ ലാഭം",
  "sip.total": "ആകെ",
  "sip.fullCalc": "പൂർണ്ണ കാൽക്കുലേറ്റർ",
  "grid.title": "ക്വിക്ക് ഫിക്സ് ഗ്രിഡ്",
  "grid.sub": "മൂന്ന് ടൂളുകൾ. മൂന്ന് പണ പ്രശ്നങ്ങൾ പരിഹരിച്ചു.",
  "grid.debtTitle": "കടം-മോചനം",
  "grid.debtDesc": "പലിശയിൽ ലക്ഷങ്ങൾ ലാഭിക്കൂ.",
  "grid.debtDetail": "ലോണുകളിൽ എത്ര അധികം നൽകുന്നുവെന്ന് കണ്ടെത്തി പ്രീപേമെന്റ് തന്ത്രം ഉണ്ടാക്കൂ.",
  "grid.taxTitle": "നികുതി ആസൂത്രകൻ",
  "grid.taxDesc": "പുതിയ vs പഴയ രീതി? 30 സെക്കൻഡിൽ കണ്ടെത്തൂ.",
  "grid.taxDetail": "നിങ്ങളുടെ വരുമാനവും കിഴിവുകളും നൽകൂ, എഞ്ചിൻ മികച്ച രീതി ശുപാർശ ചെയ്യും.",
  "grid.reTitle": "റിയൽ എസ്റ്റേറ്റ് പ്ലാനർ",
  "grid.reDesc": "ആ 2BHK ശരിക്കും വാങ്ങാൻ കഴിയുമോ?",
  "grid.reDetail": "EMI, ഡൗൺ പേമെന്റ്, സ്റ്റാമ്പ് ഡ്യൂട്ടി — എല്ലാം ചേർത്ത് യഥാർത്ഥ ചെലവ് കാണൂ.",
  "grid.openTool": "ടൂൾ തുറക്കൂ",
  "ba.title": "പരിവർത്തനം കാണൂ",
  "ba.sub": "VittaSathi നിങ്ങളുടെ ധനകാര്യം എങ്ങനെ ഒപ്റ്റിമൈസ് ചെയ്യുന്നു എന്ന് കാണൂ",
  "ba.before": "മുമ്പ്",
  "ba.after": "ശേഷം",
  "ba.beforeLabel": "VittaSathi മുമ്പ്",
  "ba.afterLabel": "VittaSathi ശേഷം",
  "ba.b1": "❌ വ്യത്യസ്ത നിരക്കുകളിൽ 3 റാൻഡം FD",
  "ba.b2": "❌ HRA ഇളവിനെ കുറിച്ച് അറിയില്ല",
  "ba.b3": "❌ ഹോം ലോണിൽ ₹8,400/മാസം അധികം നൽകുന്നു",
  "ba.b4": "❌ തെറ്റായ നികുതി രീതി — ₹22K അധികം",
  "ba.b5": "❌ അടിയന്തര ഫണ്ട് തന്ത്രം ഇല്ല",
  "ba.a1": "✅ ഹൈ-യീൽഡ് ഡെറ്റ് ഫണ്ടുകളിൽ ഏകീകരിച്ചു",
  "ba.a2": "✅ HRA ക്ലെയിം — ₹12K/മാസം ലാഭം",
  "ba.a3": "✅ പ്രീപേമെന്റ് പ്ലാനിൽ ₹6.2L പലിശ ലാഭിച്ചു",
  "ba.a4": "✅ ശരിയായ രീതി — ₹22K റീഫണ്ട്",
  "ba.a5": "✅ 6 മാസത്തെ അടിയന്തര ബഫർ ലിക്വിഡ് ഫണ്ടിൽ",
  "hack.title": "💡 ഇന്നത്തെ ഹാക്ക്",
  "hack.tag": "നികുതി ഹാക്ക് #47",
  "hack.headline": "NPS വഴി 80C-ക്ക് മുകളിൽ ₹50,000 അധിക കിഴിവ് ലഭിക്കും",
  "hack.body": "മിക്ക ശമ്പളക്കാരും 80C ₹1.5L പരിധിയിൽ നിൽക്കുന്നു. എന്നാൽ 80CCD(1B) NPS-ന് ₹50,000 അധിക കിഴിവ് നൽകുന്നു. 30% നികുതി ബ്രാക്കറ്റിൽ ഓരോ വർഷവും ₹15,600 ലാഭം.",
  "hack.readMore": "കൂടുതൽ വായിക്കൂ",
  "comp.title": "അടുത്തടുത്ത് താരതമ്യം",
  "comp.sub": "ഏത് ഉൽപ്പന്നം മികച്ചത് — ഡാറ്റ കാണൂ",
  "comp.rewards": "റിവാർഡ്സ്",
  "comp.annualFee": "വാർഷിക ഫീസ്",
  "comp.compareAll": "എല്ലാം താരതമ്യം ചെയ്യൂ",
  "wl.title": "നിങ്ങളുടെ സമ്പത്ത് ചോർച്ചകൾ കണ്ടെത്തൂ",
  "wl.sub": "നിങ്ങൾ ഒഴിവാക്കുന്ന മറഞ്ഞിരിക്കുന്ന ലാഭം കണ്ടെത്തൂ",
  "wl.label": "നിങ്ങളുടെ മാസ ശമ്പളം നൽകൂ",
  "wl.placeholder": "ഉദാ. 75,000",
  "wl.hacking": "🔍 നിങ്ങൾ നഷ്ടപ്പെടുത്തുന്ന മറഞ്ഞ ഹാക്കുകൾ",
  "wl.hra": "HRA ശരിയായി ക്ലെയിം ചെയ്ത് ₹{amount}/മാസം ലാഭിക്കാം",
  "wl.nps": "NPS (80CCD) വഴി ₹{amount}/വർഷം അധിക നികുതി കിഴിവ്",
  "wl.fd": "FD പലിശ ~₹{amount}/വർഷം നികുതിയോഗ്യം — ഡെറ്റ് ഫണ്ടുകളിലേക്ക് മാറൂ",
  "wl.deepDive": "നികുതി ഒപ്റ്റിമൈസറിൽ ആഴത്തിൽ കാണൂ",
  "nl.title": "സൗജന്യ സമ്പത്ത് ഗൈഡ് നേടൂ",
  "nl.sub": "നികുതി ഹാക്കുകൾ, SIP തന്ത്രങ്ങൾ, റിയൽ-എസ്റ്റേറ്റ് ചെക്ക്‌ലിസ്റ്റ് — 20 പേജ് PDF.",
  "nl.placeholder": "your@email.com",
  "nl.cta": "PDF ഡൗൺലോഡ്",
  "nl.success": "🎉 ഇൻബോക്സ് പരിശോധിക്കൂ! ഗൈഡ് വരുന്നു.",
  "footer.legal": "നിയമ നിരാകരണങ്ങൾ",
  "footer.privacy": "സ്വകാര്യതാ നയം",
  "footer.disclaimer": "© {year} VittaSathi. വിവരാവശ്യത്തിന് മാത്രം. ധനകാര്യ ഉപദേശമല്ല.",
};

const pa: TranslationKeys = {
  "nav.tools": "ਟੂਲਸ",
  "nav.compare": "ਤੁਲਨਾ",
  "nav.realEstate": "ਰੀਅਲ ਅਸਟੇਟ",
  "nav.taxCenter": "ਟੈਕਸ ਪਲਾਨਰ",
  "nav.dashboard": "ਡੈਸ਼ਬੋਰਡ",
  "nav.getStarted": "ਸ਼ੁਰੂ ਕਰੋ",
  "hero.badge": "ਆਧੁਨਿਕ ਭਾਰਤ ਲਈ ਸਮਾਰਟ ਵਿੱਤੀ ਟੂਲਸ",
  "hero.headline1": "ਅੰਦਾਜ਼ਾ ਲਾਉਣਾ ਬੰਦ ਕਰੋ,",
  "hero.headline2": "ਵਧਣਾ ਸ਼ੁਰੂ ਕਰੋ।",
  "hero.sub": "ਕਰਜ਼ਾ ਖ਼ਤਮ ਕਰੋ, ਟੈਕਸ ਬਚਾਓ, ਅਤੇ ਆਪਣੇ ਸੁਪਨਿਆਂ ਦੇ ਘਰ ਦੀ ਯੋਜਨਾ ਬਣਾਓ। ਆਧੁਨਿਕ ਭਾਰਤੀ ਕਮਾਉਣ ਵਾਲਿਆਂ ਲਈ ਬਣਾਇਆ ਗਿਆ।",
  "hero.cta1": "ਸ਼ੁਰੂ ਕਰੋ",
  "hero.cta2": "ਟੂਲਸ ਦੇਖੋ",
  "sip.title": "ਮਿੰਨੀ SIP ਕੈਲਕੁਲੇਟਰ",
  "sip.monthly": "ਮਹੀਨਾਵਾਰ SIP",
  "sip.period": "ਸਮਾਂ",
  "sip.yrs": "ਸਾਲ",
  "sip.invested": "ਨਿਵੇਸ਼",
  "sip.returns": "ਅੰਦਾਜ਼ਨ ਮੁਨਾਫ਼ਾ",
  "sip.total": "ਕੁੱਲ",
  "sip.fullCalc": "ਪੂਰਾ ਕੈਲਕੁਲੇਟਰ",
  "grid.title": "ਕੁਇੱਕ ਫਿਕਸ ਗ੍ਰਿੱਡ",
  "grid.sub": "ਤਿੰਨ ਟੂਲਸ। ਤਿੰਨ ਪੈਸਿਆਂ ਦੀਆਂ ਸਮੱਸਿਆਵਾਂ ਹੱਲ।",
  "grid.debtTitle": "ਕਰਜ਼ਾ ਕਿੱਲਰ",
  "grid.debtDesc": "ਵਿਆਜ ਵਿੱਚ ਲੱਖਾਂ ਬਚਾਓ।",
  "grid.debtDetail": "ਲੋਨ 'ਤੇ ਕਿੰਨਾ ਵੱਧ ਦੇ ਰਹੇ ਹੋ ਜਾਣੋ ਅਤੇ ਪ੍ਰੀਪੇਮੈਂਟ ਰਣਨੀਤੀ ਬਣਾਓ।",
  "grid.taxTitle": "ਟੈਕਸ ਪਲਾਨਰ",
  "grid.taxDesc": "ਨਵੀਂ vs ਪੁਰਾਣੀ ਰਿਜੀਮ? 30 ਸਕਿੰਟਾਂ ਵਿੱਚ ਜਾਣੋ।",
  "grid.taxDetail": "ਆਪਣੀ ਆਮਦਨ ਅਤੇ ਕਟੌਤੀਆਂ ਪਾਓ, ਇੰਜਣ ਸਭ ਤੋਂ ਵਧੀਆ ਰਿਜੀਮ ਸੁਝਾਏਗਾ।",
  "grid.reTitle": "ਰੀਅਲ ਅਸਟੇਟ ਪਲੈਨਰ",
  "grid.reDesc": "ਕੀ ਤੁਸੀਂ ਸੱਚਮੁੱਚ ਉਹ 2BHK ਖ਼ਰੀਦ ਸਕਦੇ ਹੋ?",
  "grid.reDetail": "EMI, ਡਾਊਨ ਪੇਮੈਂਟ, ਸਟੈਂਪ ਡਿਊਟੀ — ਸਭ ਜੋੜ ਕੇ ਅਸਲੀ ਖ਼ਰਚ ਦੇਖੋ।",
  "grid.openTool": "ਟੂਲ ਖੋਲੋ",
  "ba.title": "ਬਦਲਾਅ ਦੇਖੋ",
  "ba.sub": "VittaSathi ਤੁਹਾਡੀਆਂ ਵਿੱਤੀ ਮਾਮਲਿਆਂ ਨੂੰ ਕਿਵੇਂ ਅਨੁਕੂਲ ਬਣਾਉਂਦਾ ਹੈ ਦੇਖੋ",
  "ba.before": "ਪਹਿਲਾਂ",
  "ba.after": "ਬਾਅਦ ਵਿੱਚ",
  "ba.beforeLabel": "VittaSathi ਤੋਂ ਪਹਿਲਾਂ",
  "ba.afterLabel": "VittaSathi ਤੋਂ ਬਾਅਦ",
  "ba.b1": "❌ ਵੱਖ-ਵੱਖ ਦਰਾਂ 'ਤੇ 3 ਰੈਂਡਮ FD",
  "ba.b2": "❌ HRA ਛੋਟ ਬਾਰੇ ਪਤਾ ਨਹੀਂ",
  "ba.b3": "❌ ਹੋਮ ਲੋਨ 'ਤੇ ₹8,400/ਮਹੀਨਾ ਵੱਧ ਭੁਗਤਾਨ",
  "ba.b4": "❌ ਗ਼ਲਤ ਟੈਕਸ ਰਿਜੀਮ — ₹22K ਵੱਧ",
  "ba.b5": "❌ ਐਮਰਜੈਂਸੀ ਫੰਡ ਰਣਨੀਤੀ ਨਹੀਂ",
  "ba.a1": "✅ ਹਾਈ-ਯੀਲਡ ਡੈਟ ਫੰਡ ਵਿੱਚ ਇਕੱਠਾ",
  "ba.a2": "✅ HRA ਕਲੇਮ — ₹12K/ਮਹੀਨਾ ਬੱਚਤ",
  "ba.a3": "✅ ਪ੍ਰੀਪੇਮੈਂਟ ਪਲੈਨ ਨਾਲ ₹6.2L ਵਿਆਜ ਬਚਿਆ",
  "ba.a4": "✅ ਸਹੀ ਰਿਜੀਮ — ₹22K ਰਿਫੰਡ",
  "ba.a5": "✅ 6 ਮਹੀਨੇ ਦਾ ਐਮਰਜੈਂਸੀ ਬਫ਼ਰ ਲਿਕਵਿਡ ਫੰਡ ਵਿੱਚ",
  "hack.title": "💡 ਅੱਜ ਦਾ ਹੈਕ",
  "hack.tag": "ਟੈਕਸ ਹੈਕ #47",
  "hack.headline": "NPS ਤੋਂ 80C ਤੋਂ ਉੱਪਰ ₹50,000 ਵਾਧੂ ਕਟੌਤੀ ਮਿਲਦੀ ਹੈ",
  "hack.body": "ਜ਼ਿਆਦਾਤਰ ਤਨਖ਼ਾਹਦਾਰ 80C ₹1.5L ਹੱਦ 'ਤੇ ਰੁਕ ਜਾਂਦੇ ਹਨ। ਪਰ 80CCD(1B) NPS ਲਈ ₹50,000 ਵਾਧੂ ਕਟੌਤੀ ਦਿੰਦਾ ਹੈ। 30% ਟੈਕਸ ਬ੍ਰੈਕੇਟ ਵਿੱਚ ਹਰ ਸਾਲ ₹15,600 ਬੱਚਤ।",
  "hack.readMore": "ਹੋਰ ਪੜ੍ਹੋ",
  "comp.title": "ਨਾਲ-ਨਾਲ ਤੁਲਨਾ",
  "comp.sub": "ਕਿਹੜਾ ਪ੍ਰੋਡਕਟ ਬਿਹਤਰ ਹੈ — ਡਾਟਾ ਦੇਖੋ",
  "comp.rewards": "ਰਿਵਾਰਡਸ",
  "comp.annualFee": "ਸਾਲਾਨਾ ਫ਼ੀਸ",
  "comp.compareAll": "ਸਭ ਤੁਲਨਾ ਕਰੋ",
  "wl.title": "ਆਪਣੇ ਵੈਲਥ ਲੀਕ ਲੱਭੋ",
  "wl.sub": "ਲੁਕੀ ਹੋਈ ਬੱਚਤ ਲੱਭੋ ਜੋ ਤੁਸੀਂ ਛੱਡ ਰਹੇ ਹੋ",
  "wl.label": "ਆਪਣੀ ਮਹੀਨਾਵਾਰ ਤਨਖ਼ਾਹ ਦਰਜ ਕਰੋ",
  "wl.placeholder": "ਜਿਵੇਂ 75,000",
  "wl.hacking": "🔍 ਲੁਕੇ ਹੋਏ ਹੈਕ ਜੋ ਤੁਸੀਂ ਮਿਸ ਕਰ ਰਹੇ ਹੋ",
  "wl.hra": "HRA ਸਹੀ ਤਰੀਕੇ ਨਾਲ ਕਲੇਮ ਕਰਕੇ ₹{amount}/ਮਹੀਨਾ ਬਚਾ ਸਕਦੇ ਹੋ",
  "wl.nps": "NPS (80CCD) ਤੋਂ ₹{amount}/ਸਾਲ ਵਾਧੂ ਟੈਕਸ ਕਟੌਤੀ",
  "wl.fd": "FD ਵਿਆਜ ~₹{amount}/ਸਾਲ ਟੈਕਸਯੋਗ ਹੈ — ਡੈਟ ਫੰਡਾਂ ਵਿੱਚ ਬਦਲੋ",
  "wl.deepDive": "ਟੈਕਸ ਪਲਾਨਰ ਵਿੱਚ ਡੂੰਘਾਈ ਨਾਲ ਜਾਣੋ",
  "nl.title": "ਮੁਫ਼ਤ ਵੈਲਥ ਗਾਈਡ ਲਵੋ",
  "nl.sub": "ਟੈਕਸ ਹੈਕ, SIP ਰਣਨੀਤੀਆਂ, ਰੀਅਲ-ਅਸਟੇਟ ਚੈਕਲਿਸਟ — 20 ਪੰਨਿਆਂ ਦੀ PDF।",
  "nl.placeholder": "your@email.com",
  "nl.cta": "PDF ਡਾਊਨਲੋਡ",
  "nl.success": "🎉 ਇਨਬਾਕਸ ਚੈਕ ਕਰੋ! ਗਾਈਡ ਆ ਰਹੀ ਹੈ।",
  "footer.legal": "ਕਾਨੂੰਨੀ ਬੇਦਾਅਵਾ",
  "footer.privacy": "ਗੋਪਨੀਅਤਾ ਨੀਤੀ",
  "footer.disclaimer": "© {year} VittaSathi। ਸਿਰਫ਼ ਜਾਣਕਾਰੀ ਲਈ। ਵਿੱਤੀ ਸਲਾਹ ਨਹੀਂ।",
};

const or: TranslationKeys = {
  "nav.tools": "ଟୁଲ୍ସ",
  "nav.compare": "ତୁଳନା",
  "nav.realEstate": "ରିଆଲ ଇଷ୍ଟେଟ",
  "nav.taxCenter": "ଟ୍ୟାକ୍ସ ପ୍ଲାନର୍",
  "nav.dashboard": "ଡ୍ୟାସବୋର୍ଡ",
  "nav.getStarted": "ଆରମ୍ଭ କରନ୍ତୁ",
  "hero.badge": "ଆଧୁନିକ ଭାରତ ପାଇଁ ସ୍ମାର୍ଟ ଆର୍ଥିକ ଟୁଲ୍ସ",
  "hero.headline1": "ଅନୁମାନ ବନ୍ଦ କରନ୍ତୁ,",
  "hero.headline2": "ବଢ଼ିବା ଆରମ୍ଭ କରନ୍ତୁ।",
  "hero.sub": "ଋଣ ଶେଷ କରନ୍ତୁ, ଟ୍ୟାକ୍ସ ସଞ୍ଚୟ କରନ୍ତୁ, ଏବଂ ସ୍ୱପ୍ନର ଘର ଯୋଜନା କରନ୍ତୁ। ଆଧୁନିକ ଭାରତୀୟ ରୋଜଗାରକାରୀଙ୍କ ପାଇଁ ନିର୍ମିତ।",
  "hero.cta1": "ଆରମ୍ଭ କରନ୍ତୁ",
  "hero.cta2": "ଟୁଲ୍ସ ଦେଖନ୍ତୁ",
  "sip.title": "ମିନି SIP କ୍ୟାଲ୍କୁଲେଟର",
  "sip.monthly": "ମାସିକ SIP",
  "sip.period": "ସମୟ ଅବଧି",
  "sip.yrs": "ବର୍ଷ",
  "sip.invested": "ନିବେଶ",
  "sip.returns": "ଆନୁମାନିକ ରିଟର୍ଣ୍ଣ",
  "sip.total": "ମୋଟ",
  "sip.fullCalc": "ସମ୍ପୂର୍ଣ୍ଣ କ୍ୟାଲ୍କୁଲେଟର",
  "grid.title": "କ୍ୱିକ୍ ଫିକ୍ସ ଗ୍ରିଡ୍",
  "grid.sub": "ତିନିଟି ଟୁଲ୍ସ। ତିନିଟି ଟଙ୍କା ସମସ୍ୟା ସମାଧାନ।",
  "grid.debtTitle": "ଋଣ କିଲର",
  "grid.debtDesc": "ସୁଧରେ ଲକ୍ଷ ଲକ୍ଷ ସଞ୍ଚୟ କରନ୍ତୁ।",
  "grid.debtDetail": "ଲୋନରେ କେତେ ଅଧିକ ଦେଉଛନ୍ତି ଜାଣନ୍ତୁ ଏବଂ ପ୍ରୀପେମେଣ୍ଟ ରଣନୀତି ତିଆରି କରନ୍ତୁ।",
  "grid.taxTitle": "ଟ୍ୟାକ୍ସ ପ୍ଲାନର୍",
  "grid.taxDesc": "ନୂଆ vs ପୁରୁଣା ରିଜିମ? 30 ସେକେଣ୍ଡରେ ଜାଣନ୍ତୁ।",
  "grid.taxDetail": "ଆପଣଙ୍କ ଆୟ ଓ କଟୌତି ଦିଅନ୍ତୁ, ଇଞ୍ଜିନ ସର୍ବୋତ୍ତମ ରିଜିମ ସୁପାରିଶ କରିବ।",
  "grid.reTitle": "ରିଆଲ ଇଷ୍ଟେଟ ପ୍ଲାନର",
  "grid.reDesc": "ସେହି 2BHK ସତରେ କିଣି ପାରିବେ କି?",
  "grid.reDetail": "EMI, ଡାଉନ ପେମେଣ୍ଟ, ଷ୍ଟାମ୍ପ ଡ୍ୟୁଟି — ସବୁ ଯୋଡ଼ି ଅସଲ ଖର୍ଚ୍ଚ ଦେଖନ୍ତୁ।",
  "grid.openTool": "ଟୁଲ୍ ଖୋଲନ୍ତୁ",
  "ba.title": "ପରିବର୍ତ୍ତନ ଦେଖନ୍ତୁ",
  "ba.sub": "VittaSathi ଆପଣଙ୍କ ଆର୍ଥିକ ବ୍ୟବସ୍ଥାକୁ କିପରି ଅପ୍ଟିମାଇଜ କରେ ଦେଖନ୍ତୁ",
  "ba.before": "ଆଗରୁ",
  "ba.after": "ପରେ",
  "ba.beforeLabel": "VittaSathi ଆଗରୁ",
  "ba.afterLabel": "VittaSathi ପରେ",
  "ba.b1": "❌ ଭିନ୍ନ ଭିନ୍ନ ହାରରେ 3ଟି ରାଣ୍ଡମ FD",
  "ba.b2": "❌ HRA ଛାଡ଼ ବିଷୟରେ ଜାଣନ୍ତି ନାହିଁ",
  "ba.b3": "❌ ହୋମ ଲୋନରେ ₹8,400/ମାସ ଅଧିକ ଦେଉଛନ୍ତି",
  "ba.b4": "❌ ଭୁଲ ଟ୍ୟାକ୍ସ ରିଜିମ — ₹22K ଅଧିକ",
  "ba.b5": "❌ ଜରୁରୀ ଫଣ୍ଡ ରଣନୀତି ନାହିଁ",
  "ba.a1": "✅ ହାଇ-ୟିଲ୍ଡ ଡେଟ୍ ଫଣ୍ଡରେ ଏକତ୍ରିତ",
  "ba.a2": "✅ HRA କ୍ଲେମ — ₹12K/ମାସ ସଞ୍ଚୟ",
  "ba.a3": "✅ ପ୍ରୀପେମେଣ୍ଟ ପ୍ଲାନରେ ₹6.2L ସୁଧ ସଞ୍ଚୟ",
  "ba.a4": "✅ ସଠିକ ରିଜିମ — ₹22K ରିଫଣ୍ଡ",
  "ba.a5": "✅ 6 ମାସର ଜରୁରୀ ବଫର ଲିକୁଇଡ ଫଣ୍ଡରେ",
  "hack.title": "💡 ଆଜିର ହ୍ୟାକ୍",
  "hack.tag": "ଟ୍ୟାକ୍ସ ହ୍ୟାକ୍ #47",
  "hack.headline": "NPS ଦ୍ୱାରା 80C ଉପରେ ₹50,000 ଅତିରିକ୍ତ କଟୌତି ମିଳେ",
  "hack.body": "ଅଧିକାଂଶ ଚାକିରିଆ 80C ₹1.5L ସୀମାରେ ଅଟକି ଯାଆନ୍ତି। କିନ୍ତୁ 80CCD(1B) NPS ପାଇଁ ₹50,000 ଅତିରିକ୍ତ କଟୌତି ଦିଏ। 30% ଟ୍ୟାକ୍ସ ବ୍ରାକେଟରେ ପ୍ରତିବର୍ଷ ₹15,600 ସଞ୍ଚୟ।",
  "hack.readMore": "ଆହୁରି ପଢ଼ନ୍ତୁ",
  "comp.title": "ପାଖାପାଖି ତୁଳନା",
  "comp.sub": "କେଉଁ ଉତ୍ପାଦ ଭଲ — ଡାଟା ଦେଖନ୍ତୁ",
  "comp.rewards": "ରିୱାର୍ଡ୍ସ",
  "comp.annualFee": "ବାର୍ଷିକ ଫି",
  "comp.compareAll": "ସବୁ ତୁଳନା କରନ୍ତୁ",
  "wl.title": "ଆପଣଙ୍କ ୱେଲ୍ଥ ଲିକ୍ ଖୋଜନ୍ତୁ",
  "wl.sub": "ଲୁଚି ଥିବା ସଞ୍ଚୟ ଖୋଜନ୍ତୁ",
  "wl.label": "ଆପଣଙ୍କ ମାସିକ ଦରମା ଦିଅନ୍ତୁ",
  "wl.placeholder": "ଯଥା 75,000",
  "wl.hacking": "🔍 ଲୁଚି ଥିବା ହ୍ୟାକ୍ସ ଯାହା ଆପଣ ମିସ୍ କରୁଛନ୍ତି",
  "wl.hra": "HRA ସଠିକ ଭାବେ କ୍ଲେମ କରି ₹{amount}/ମାସ ସଞ୍ଚୟ କରିପାରିବେ",
  "wl.nps": "NPS (80CCD) ଦ୍ୱାରା ₹{amount}/ବର୍ଷ ଅତିରିକ୍ତ ଟ୍ୟାକ୍ସ କଟୌତି",
  "wl.fd": "FD ସୁଧ ~₹{amount}/ବର୍ଷ ଟ୍ୟାକ୍ସଯୋଗ୍ୟ — ଡେଟ୍ ଫଣ୍ଡରେ ବଦଳାନ୍ତୁ",
  "wl.deepDive": "ଟ୍ୟାକ୍ସ ପ୍ଲାନର୍ରେ ଗଭୀରକୁ ଯାଆନ୍ତୁ",
  "nl.title": "ମାଗଣା ୱେଲ୍ଥ ଗାଇଡ୍ ପାଆନ୍ତୁ",
  "nl.sub": "ଟ୍ୟାକ୍ସ ହ୍ୟାକ୍ସ, SIP ରଣନୀତି, ରିଆଲ-ଇଷ୍ଟେଟ ଚେକ୍‌ଲିଷ୍ଟ — 20 ପୃଷ୍ଠାର PDF।",
  "nl.placeholder": "your@email.com",
  "nl.cta": "PDF ଡାଉନଲୋଡ",
  "nl.success": "🎉 ଇନବକ୍ସ ଚେକ୍ କରନ୍ତୁ! ଗାଇଡ୍ ଆସୁଛି।",
  "footer.legal": "ଆଇନଗତ ଅସ୍ୱୀକରଣ",
  "footer.privacy": "ଗୋପନୀୟତା ନୀତି",
  "footer.disclaimer": "© {year} VittaSathi। କେବଳ ସୂଚନାମୂଳକ ଉଦ୍ଦେଶ୍ୟ ପାଇଁ। ଆର୍ଥିକ ପରାମର୍ଶ ନୁହେଁ।",
};

const as_: TranslationKeys = {
  "nav.tools": "সঁজুলিসমূহ",
  "nav.compare": "তুলনা",
  "nav.realEstate": "ৰিয়েল এষ্টেট",
  "nav.taxCenter": "কৰ পৰিকল্পক",
  "nav.dashboard": "ডেশ্বব'ৰ্ড",
  "nav.getStarted": "আৰম্ভ কৰক",
  "hero.badge": "আধুনিক ভাৰতৰ বাবে স্মাৰ্ট বিত্তীয় সঁজুলি",
  "hero.headline1": "অনুমান কৰা বন্ধ কৰক,",
  "hero.headline2": "বাঢ়িবলৈ আৰম্ভ কৰক।",
  "hero.sub": "ঋণ শেষ কৰক, কৰ ৰাহি কৰক, আৰু সপোনৰ ঘৰ পৰিকল্পনা কৰক। আধুনিক ভাৰতীয় উপাৰ্জনকাৰীসকলৰ বাবে নিৰ্মিত।",
  "hero.cta1": "আৰম্ভ কৰক",
  "hero.cta2": "সঁজুলি চাওক",
  "sip.title": "মিনি SIP কেলকুলেটৰ",
  "sip.monthly": "মাহিলী SIP",
  "sip.period": "সময় কাল",
  "sip.yrs": "বছৰ",
  "sip.invested": "বিনিয়োগ",
  "sip.returns": "আনুমানিক লাভ",
  "sip.total": "মুঠ",
  "sip.fullCalc": "সম্পূৰ্ণ কেলকুলেটৰ",
  "grid.title": "কুইক ফিক্স গ্ৰিড",
  "grid.sub": "তিনিটা সঁজুলি। তিনিটা ধনৰ সমস্যা সমাধান।",
  "grid.debtTitle": "ঋণ কিলাৰ",
  "grid.debtDesc": "সুদত লাখ লাখ ৰাহি কৰক।",
  "grid.debtDetail": "ঋণত কিমান বেছি দিছে জানক আৰু প্ৰিপেমেণ্ট কৌশল তৈয়াৰ কৰক।",
  "grid.taxTitle": "কৰ পৰিকল্পক",
  "grid.taxDesc": "নতুন vs পুৰণি ৰিজিম? 30 ছেকেণ্ডত জানক।",
  "grid.taxDetail": "আপোনাৰ আয় আৰু কৰ্তন দিয়ক, ইঞ্জিনে উত্তম ৰিজিম পৰামৰ্শ দিব।",
  "grid.reTitle": "ৰিয়েল এষ্টেট প্লেনাৰ",
  "grid.reDesc": "সেই 2BHK সঁচাকৈয়ে কিনিব পাৰিব নে?",
  "grid.reDetail": "EMI, ডাউন পেমেণ্ট, ষ্টাম্প ডিউটি — সকলো যোগ কৰি প্ৰকৃত খৰচ চাওক।",
  "grid.openTool": "সঁজুলি খোলক",
  "ba.title": "পৰিৱৰ্তন চাওক",
  "ba.sub": "VittaSathi-ই আপোনাৰ বিত্তীয় ব্যৱস্থা কেনেকৈ অপ্টিমাইজ কৰে চাওক",
  "ba.before": "আগতে",
  "ba.after": "পিছত",
  "ba.beforeLabel": "VittaSathi আগতে",
  "ba.afterLabel": "VittaSathi পিছত",
  "ba.b1": "❌ বেলেগ বেলেগ হাৰত 3টা ৰেণ্ডম FD",
  "ba.b2": "❌ HRA ছাড়ৰ বিষয়ে জ্ঞান নাই",
  "ba.b3": "❌ হোম লোনত ₹8,400/মাহ অধিক দিছে",
  "ba.b4": "❌ ভুল কৰ ৰিজিম — ₹22K অধিক",
  "ba.b5": "❌ জৰুৰীকালীন পুঁজি কৌশল নাই",
  "ba.a1": "✅ হাই-ইল্ড ডেট ফাণ্ডত একত্ৰিত",
  "ba.a2": "✅ HRA দাবী — ₹12K/মাহ ৰাহি",
  "ba.a3": "✅ প্ৰিপেমেণ্ট প্লেনত ₹6.2L সুদ ৰাহি",
  "ba.a4": "✅ সঠিক ৰিজিম — ₹22K ৰিফাণ্ড",
  "ba.a5": "✅ 6 মাহৰ জৰুৰীকালীন বাফাৰ লিকুইড ফাণ্ডত",
  "hack.title": "💡 আজিৰ হেক",
  "hack.tag": "কৰ হেক #47",
  "hack.headline": "NPS-ৰ পৰা 80C-ৰ ওপৰত ₹50,000 অতিৰিক্ত কৰ্তন পাব",
  "hack.body": "বেছিভাগ দৰমহা পোৱা লোকে 80C ₹1.5L সীমাত ৰৈ যায়। কিন্তু 80CCD(1B)-এ NPS-ৰ বাবে ₹50,000 অতিৰিক্ত কৰ্তন দিয়ে। 30% কৰ ব্ৰেকেটত প্ৰতি বছৰে ₹15,600 ৰাহি।",
  "hack.readMore": "অধিক পঢ়ক",
  "comp.title": "কাষে-কাষে তুলনা",
  "comp.sub": "কোনটো প্ৰডাক্ট ভাল — ডাটা চাওক",
  "comp.rewards": "ৰিৱাৰ্ডছ",
  "comp.annualFee": "বাৰ্ষিক মাচুল",
  "comp.compareAll": "সকলো তুলনা কৰক",
  "wl.title": "আপোনাৰ ৱেলথ লিক বিচাৰক",
  "wl.sub": "লুকাই থকা ৰাহি বিচাৰক",
  "wl.label": "আপোনাৰ মাহিলী দৰমহা দিয়ক",
  "wl.placeholder": "যেনে 75,000",
  "wl.hacking": "🔍 লুকাই থকা হেকছ যিবোৰ আপুনি মিছ কৰিছে",
  "wl.hra": "HRA সঠিককৈ দাবী কৰি ₹{amount}/মাহ ৰাহি কৰিব পাৰে",
  "wl.nps": "NPS (80CCD)-ৰ পৰা ₹{amount}/বছৰ অতিৰিক্ত কৰ কৰ্তন",
  "wl.fd": "FD সুদ ~₹{amount}/বছৰ কৰযোগ্য — ডেট ফাণ্ডলৈ সলনি কৰক",
  "wl.deepDive": "কৰ পৰিকল্পকত গভীৰভাৱে চাওক",
  "nl.title": "বিনামূলীয়া ৱেলথ গাইড পাওক",
  "nl.sub": "কৰ হেকছ, SIP কৌশল, ৰিয়েল-এষ্টেট চেকলিষ্ট — 20 পৃষ্ঠাৰ PDF।",
  "nl.placeholder": "your@email.com",
  "nl.cta": "PDF ডাউনলোড",
  "nl.success": "🎉 ইনবক্স চেক কৰক! গাইড আহি আছে।",
  "footer.legal": "আইনী অস্বীকৃতি",
  "footer.privacy": "গোপনীয়তা নীতি",
  "footer.disclaimer": "© {year} VittaSathi। কেৱল তথ্যমূলক উদ্দেশ্যে। বিত্তীয় পৰামৰ্শ নহয়।",
};

const ur: TranslationKeys = {
  "nav.tools": "ٹولز",
  "nav.compare": "موازنہ",
  "nav.realEstate": "ریئل اسٹیٹ",
  "nav.taxCenter": "ٹیکس پلانر",
  "nav.dashboard": "ڈیش بورڈ",
  "nav.getStarted": "شروع کریں",
  "hero.badge": "جدید بھارت کے لیے سمارٹ مالی ٹولز",
  "hero.headline1": "اندازہ لگانا بند کرو،",
  "hero.headline2": "بڑھنا شروع کرو۔",
  "hero.sub": "قرض ختم کرو، ٹیکس بچاؤ، اور اپنے خوابوں کے گھر کی منصوبہ بندی کرو۔ جدید ہندوستانی کمانے والوں کے لیے بنایا گیا۔",
  "hero.cta1": "شروع کریں",
  "hero.cta2": "ٹولز دیکھیں",
  "sip.title": "منی SIP کیلکولیٹر",
  "sip.monthly": "ماہانہ SIP",
  "sip.period": "مدت",
  "sip.yrs": "سال",
  "sip.invested": "سرمایہ کاری",
  "sip.returns": "تخمینی منافع",
  "sip.total": "کل",
  "sip.fullCalc": "مکمل کیلکولیٹر",
  "grid.title": "کوئک فکس گرڈ",
  "grid.sub": "تین ٹولز۔ تین پیسوں کے مسائل حل۔",
  "grid.debtTitle": "قرض کلر",
  "grid.debtDesc": "سود میں لاکھوں بچاؤ۔",
  "grid.debtDetail": "لون پر کتنا زیادہ دے رہے ہو جانو اور پری پیمنٹ حکمت عملی بناؤ۔",
  "grid.taxTitle": "ٹیکس پلانر",
  "grid.taxDesc": "نئی vs پرانی ریجیم؟ 30 سیکنڈ میں جانو۔",
  "grid.taxDetail": "اپنی آمدنی اور کٹوتیاں ڈالو، انجن بہترین ریجیم تجویز کرے گا۔",
  "grid.reTitle": "ریئل اسٹیٹ پلانر",
  "grid.reDesc": "کیا آپ واقعی وہ 2BHK خرید سکتے ہو؟",
  "grid.reDetail": "EMI، ڈاؤن پیمنٹ، اسٹامپ ڈیوٹی — سب جوڑ کر اصل خرچ دیکھو۔",
  "grid.openTool": "ٹول کھولیں",
  "ba.title": "تبدیلی دیکھو",
  "ba.sub": "VittaSathi آپ کے مالی معاملات کو کیسے بہتر بناتا ہے دیکھو",
  "ba.before": "پہلے",
  "ba.after": "بعد میں",
  "ba.beforeLabel": "VittaSathi سے پہلے",
  "ba.afterLabel": "VittaSathi کے بعد",
  "ba.b1": "❌ مختلف شرحوں پر 3 رینڈم FD",
  "ba.b2": "❌ HRA چھوٹ کے بارے میں پتا نہیں",
  "ba.b3": "❌ ہوم لون پر ₹8,400/ماہ زیادہ ادائیگی",
  "ba.b4": "❌ غلط ٹیکس ریجیم — ₹22K زیادہ",
  "ba.b5": "❌ ایمرجنسی فنڈ حکمت عملی نہیں",
  "ba.a1": "✅ ہائی ییلڈ ڈیٹ فنڈ میں جمع",
  "ba.a2": "✅ HRA کلیم — ₹12K/ماہ بچت",
  "ba.a3": "✅ پری پیمنٹ پلان سے ₹6.2L سود بچا",
  "ba.a4": "✅ صحیح ریجیم — ₹22K ریفنڈ",
  "ba.a5": "✅ 6 ماہ کا ایمرجنسی بفر لکوڈ فنڈ میں",
  "hack.title": "💡 آج کا ہیک",
  "hack.tag": "ٹیکس ہیک #47",
  "hack.headline": "NPS سے 80C سے اوپر ₹50,000 اضافی کٹوتی ملتی ہے",
  "hack.body": "زیادہ تر تنخواہ دار 80C کی ₹1.5L حد پر رک جاتے ہیں۔ لیکن 80CCD(1B) NPS کے لیے ₹50,000 اضافی کٹوتی دیتا ہے۔ 30% ٹیکس بریکٹ میں ہر سال ₹15,600 بچت۔",
  "hack.readMore": "مزید پڑھیں",
  "comp.title": "ساتھ ساتھ موازنہ",
  "comp.sub": "کون سا پروڈکٹ بہتر ہے — ڈیٹا دیکھو",
  "comp.rewards": "ریوارڈز",
  "comp.annualFee": "سالانہ فیس",
  "comp.compareAll": "سب کا موازنہ کرو",
  "wl.title": "اپنے ویلتھ لیکس تلاش کرو",
  "wl.sub": "چھپی ہوئی بچت تلاش کرو جو آپ چھوڑ رہے ہو",
  "wl.label": "اپنی ماہانہ تنخواہ درج کریں",
  "wl.placeholder": "مثلاً 75,000",
  "wl.hacking": "🔍 چھپے ہوئے ہیکس جو آپ مس کر رہے ہو",
  "wl.hra": "HRA صحیح طریقے سے کلیم کر کے ₹{amount}/ماہ بچا سکتے ہو",
  "wl.nps": "NPS (80CCD) سے ₹{amount}/سال اضافی ٹیکس کٹوتی",
  "wl.fd": "FD سود ~₹{amount}/سال ٹیکس قابل ہے — ڈیٹ فنڈز میں شفٹ کرو",
  "wl.deepDive": "ٹیکس پلانر میں گہرائی سے جانو",
  "nl.title": "مفت ویلتھ گائیڈ حاصل کرو",
  "nl.sub": "ٹیکس ہیکس، SIP حکمت عملی، ریئل اسٹیٹ چیک لسٹ — 20 صفحات کی PDF۔",
  "nl.placeholder": "your@email.com",
  "nl.cta": "PDF ڈاؤنلوڈ",
  "nl.success": "🎉 ان باکس چیک کرو! گائیڈ آ رہی ہے۔",
  "footer.legal": "قانونی دستبرداری",
  "footer.privacy": "رازداری پالیسی",
  "footer.disclaimer": "© {year} VittaSathi۔ صرف معلوماتی مقاصد کے لیے۔ مالی مشورہ نہیں۔",
};

// Context
type LanguageContextType = {
  lang: LangCode;
  setLang: (l: LangCode) => void;
  t: (key: string, vars?: Record<string, string>) => string;
};

const translations: Record<LangCode, TranslationKeys> = {
  en, hi, bn, te, mr, ta, gu, kn, ml, pa, or: or, as: as_, ur,
};

const LanguageContext = createContext<LanguageContextType>({
  lang: "en",
  setLang: () => {},
  t: (key) => key,
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<LangCode>(() => {
    const saved = localStorage.getItem("finseva-lang");
    return (saved as LangCode) || "en";
  });

  const t = (key: string, vars?: Record<string, string>) => {
    let str = (translations[lang] as any)?.[key] 
      || innerPageTranslations[lang]?.[key]
      || homePageTranslations[lang]?.[key]
      || toolTranslations[lang]?.[key]
      || legalTranslations[lang]?.[key]
      || guideTranslations[lang]?.[key]
      || academyTranslations[lang]?.[key]
      || curriculumTranslations[lang]?.[key]
      || (translations.en as any)?.[key] 
      || innerPageTranslations.en?.[key]
      || homePageTranslations.en?.[key]
      || toolTranslations.en?.[key]
      || legalTranslations.en?.[key]
      || guideTranslations.en?.[key]
      || academyTranslations.en?.[key]
      || curriculumTranslations.en?.[key]
      || key;
    if (vars) {
      Object.entries(vars).forEach(([k, v]) => {
        str = str.replace(`{${k}}`, v);
      });
    }
    return str;
  };

  const handleSetLang = (l: LangCode) => {
    setLang(l);
    localStorage.setItem("finseva-lang", l);
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang: handleSetLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
